import type { VNode } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { Chassis } from '@vowric/ui';
import { useHashRoute } from './routes/useHashRoute';
import {
  parseRoute,
  routePath,
  topTab,
  defaultRouteFor,
  parentRoute,
  routeLabel,
  AUDITION_STAGES,
  PROJECT_PHASES,
  CAREER_SECTIONS,
  AA_MATERIALS_SECTIONS,
  MATERIALS_SECTIONS,
  packVerticalOf,
  screenOwnedBy,
  foldIntoPersonal,
  isBuilderProjectSlug,
  type Route,
} from './routes/route';
import { NavZone } from './components/NavZone';
import { SideRail } from './components/SideRail';
import { CockpitTitleSwitcher } from './components/CockpitTitleSwitcher';
import { CockpitHomeMark } from './components/CockpitHomeMark';
import { cockpitSurfaces } from './lib/cockpitSurfaces';
import { MasterDetail } from './components/MasterDetail';
import { Strip } from './components/Strip';
import { CaptureSheet } from './components/CaptureSheet';
import { KnotConfirmDialog } from './components/KnotConfirmDialog';
import { RecordEditorHost } from './components/RecordEditor';
import { GlobalCreate } from './components/GlobalCreate';
import { FindEntry, FindSheet } from './components/FindSheet';
import { AccountEntry } from './components/AccountEntry';
import { FrameBackProvider } from './components/FrameBack';
import { OperatorSkin } from './components/OperatorSkin';
import { MoreSheet } from './components/MoreSheet';
import { NavUnresolved } from './components/NavUnresolved';
import { useBreakpoint, isRail } from './hooks/useBreakpoint';
// S1 — the cockpit shell + atlas-cockpit column (spec COCKPIT-ASSISTANT-
// SPEC.md's "S1"). Personal pack only; see wrapCockpit below.
import { CockpitFrame } from './components/CockpitFrame';
import { PlaceholderScreen } from './screens/PlaceholderScreen';
import { Today } from './screens/Today';
// B16a — the cockpit screens (the personal pack's surface; spec
// `the-cockpit-surface-b16-spec-2026-08-24`).
import { CockpitToday } from './screens/CockpitToday';
import { CockpitHome } from './screens/CockpitHome';
import { ProjectScreen } from './screens/ProjectScreen';
import { Threads, threadStateOptions } from './screens/Threads';
import { Calendar } from './screens/Calendar';
import { CockpitCalendar } from './screens/CockpitCalendar';
import { Mail } from './screens/Mail';
import { CalendarSpread } from './screens/CalendarSpread';
import { Todos, todoStateOptions } from './screens/Todos';
import { Personal } from './screens/Personal';
// The Audience tab (decision `4804fc96`, spec `the-audience-build-spec-
// 2026-10-08`). AudienceInboxDetail is a separate route/screen (same
// convention as ThreadSpread beside Threads), never nested inside Audience.
import { Audience } from './screens/Audience';
import { AudienceInboxDetail } from './screens/AudienceInbox';
import { Delivery } from './screens/Delivery';
import { Programme } from './screens/Programme';
import { Systems } from './screens/Systems';
import { ClientPortal } from './screens/ClientPortal';
import { ClientBuckets } from './screens/ClientBuckets';
import { AgreementsList } from './screens/AgreementsList';
import { AgreementSpread } from './screens/AgreementSpread';
import { AccountSpread } from './screens/AccountSpread';
import { EngagementSpread } from './screens/EngagementSpread';
import { People } from './screens/People';
import { PersonSpread } from './screens/PersonSpread';
import { ThreadSpread } from './screens/ThreadSpread';
import { Review } from './screens/Review';
import { RadarHome } from './screens/RadarHome';
import { RadarTopic } from './screens/RadarTopic';
import { RadarProposal } from './screens/RadarProposal';
import { RadarTopics } from './screens/RadarTopics';
import { RadarNewsletter } from './screens/RadarNewsletter';
import { RecordHome } from './screens/RecordHome';
import { RecordPage } from './screens/RecordPage';
import { RecordFile } from './screens/RecordFile';
import { RecordBrowse } from './screens/RecordBrowse';
import { RecordReading } from './screens/RecordReading';
import { RexxHome } from './screens/RexxHome';
import { HouseholdHome } from './screens/HouseholdHome';
import { HouseholdStats } from './screens/HouseholdStats';
import { CraftPanel } from './components/CraftPanel';
import { Registry } from './screens/Registry';
import { householdTab } from './lib/householdTabs';
// A4 — the Project > Role > Audition surfaces:
import { HouseholdAuditions } from './screens/HouseholdAuditions';
import { HouseholdProjects } from './screens/HouseholdProjects';
import { ProjectSpine } from './screens/ProjectSpine';
import { RoleSurface } from './screens/RoleSurface';
import { EngagementSurface } from './screens/EngagementSurface';
import { HouseholdMoney } from './screens/HouseholdMoney';
import { HouseholdTapes } from './screens/HouseholdTapes';
import { MaterialsPhotos } from './screens/MaterialsPhotos';
import { MaterialsReelsAssist } from './screens/MaterialsReelsAssist';
import { TapeRecord } from './screens/TapeRecord';
import { ShootDayCard } from './screens/ShootDayCard';
import { AuditionSpread } from './screens/AuditionSpread';
import { HouseholdPeople } from './screens/HouseholdPeople';
import { HouseholdPerson } from './screens/HouseholdPerson';
import { HouseholdCalendar } from './screens/HouseholdCalendar';
import { HouseholdMail } from './screens/HouseholdMail';
import { HouseholdNotes } from './screens/HouseholdNotes';
import { SubmissionSpread } from './screens/SubmissionSpread';
import { YourPage } from './screens/YourPage';
import { WorkOrders } from './screens/WorkOrders';
import { Auditions } from './screens/Auditions';
import { AuditionDetail } from './screens/AuditionDetail';
import { Projects } from './screens/Projects';
import { ProjectDetail } from './screens/ProjectDetail';
import { SourceViewer } from './screens/SourceViewer';
import { Career } from './screens/Career';
import { Materials } from './screens/Materials';
import { PerformerResume } from './screens/PerformerResume';
import { Kit } from './screens/Kit';
import { RecordsHarness } from './screens/RecordsHarness';
import { Login } from './screens/Login';
import { TenantResolver } from './screens/TenantResolver';
import { FirstRun } from './screens/FirstRun';
import { Settings } from './screens/Settings';
import { ReviewerPortal } from './screens/ReviewerPortal';
// B1 — the beta door's invite screen (#/beta/<token>), reached with no
// session and no tenant, exactly like ReviewerPortal above.
import { BetaInvite } from './screens/BetaInvite';
import { CoachShell } from './screens/CoachShell';
import { CoachIntakeLanding } from './screens/CoachIntakeLanding';
import { RecordGrantPortal } from './screens/RecordGrantPortal';
import { EdenOnboarding } from './screens/EdenOnboarding';
// ---- SWARM SEAM (S2/S3/S4, 2026-08-10): three chunks land their screens
// concurrently and this import block is the one line-range they would all
// have fought over. Each owns exactly ONE marker below and puts its own
// imports directly under it — never under another chunk's marker, never
// interleaved into the block above. ----
// S2 — the work-order spread:
import { WorkOrderSpread } from './screens/WorkOrderSpread';
// S3 — the dispatch console:
import { Dispatch } from './screens/Dispatch';
// S4 — the registry drilldowns:
import { Clients } from './screens/Clients';
import { ClientDetail } from './screens/ClientDetail';
import { SiteDetail } from './screens/SiteDetail';
import { Techs } from './screens/Techs';
import { TechDetail } from './screens/TechDetail';
// S11 — the asks inbox (no rail slot; reached from Today and from receipts):
import { Asks } from './screens/Asks';
// S5a — the tech's two screens (wf03): the day, and the job.
import { TechToday } from './screens/TechToday';
import { Job } from './screens/Job';
// S5b — the conversation surface:
import { Conversations } from './screens/Conversations';
import { TechChat } from './screens/TechChat';
import type { KnotState } from './components/Knot';
import { useNavModel } from './lib/useNavDestinations';
import { Onboarding } from './screens/Onboarding';
import { primaryNavRole } from './components/navModel';
import { useKnotPushToTalk } from './lib/useKnotPushToTalk';
import { bumpRecordsVersion, openCreateMenu, openRecordEditor } from './lib/recordEditor';
import type { OutboxEntry } from './lib/outbox';
import {
  whoami,
  verifyMagicLink,
  acceptInvite,
  listTenants,
  getFirstRun,
  getEntryConfig,
  getDeploymentVertical,
  getCaptureSubjectTypes,
  getKnotIntentEnabled,
  listCommsConversations,
  countPendingProposals,
  getAudienceSummary,
  type ApiUser,
  type CaptureContext,
  getPortalMe,
  type PortalMeApi,
  ApiError,
} from './lib/api';
// CV6b — the three pieces of "the phone buzzes and the app remembers" the
// SHELL owns: the badge's number, the boot-time re-registration, and the
// worker's line into this window.
import { publishThreadUnread, threadKey } from './lib/unread';
import { captureSubjectTypeFor, screenHasCaptureSubject } from './lib/captureSubject';
import { useUnreadTotal } from './hooks/useUnread';
import { reregisterPushOnBoot } from './lib/pushEnrolment';
import { onServiceWorkerPushMessage } from './lib/swMessages';
import {
  bootstrapTenantFromUrl,
  clearStoredTenantId,
  getStoredTenantId,
  getStoredTenantVertical,
  setStoredTenantId,
  setStoredTenantVertical,
  getShellHint,
  setShellHint,
} from './lib/tenant';

// The literal path a real magic-link email's clicked GET lands on
// (packages/server/src/config.ts's MAGIC_LINK_VERIFY_PATH — same string,
// duplicated here since this package doesn't depend on @vowric/server;
// packages/server/src/plugins/staticWeb.ts's own header documents the
// server-side carve-out that makes GETting this path serve THIS app
// instead of a 404, specifically so this file can read `?token=` off it).
const MAGIC_LINK_VERIFY_PATH = '/auth/magic-link/verify';

// C6b: the invite-accept landing — the literal path a real invite email's
// clicked GET lands on (packages/server/src/config.ts's INVITE_ACCEPT_PATH,
// same string, duplicated for the same no-backend-dependency reason as the
// magic-link constant above; staticWeb.ts carves this path out so the GET
// serves this app instead of a JSON 404).
const INVITE_ACCEPT_PATH = '/auth/invites/accept';

// C6b: which verticals may carry a pack-declared first-run script — the
// SKINNED_VERTICALS convention (lib/skin.ts) one gate over: a web-side
// registry that says "ask the server", never the answer itself. Verticals
// not listed here (Actor, Personal) make ZERO first-run requests and keep
// their flows byte-unchanged; for a listed one the server's own
// GET /api/first-run is the authority (absent route / complete walk both
// land in the ordinary shell).
const FIRST_RUN_VERTICALS = ['rexx'] as const;

type FirstRunStatus = 'unknown' | 'checking' | 'pending' | 'clear';

// S1/A3/B16a, superseded 2026-09-01 by the OWNER REGISTRY. The four gate
// sets that used to live here (REXX_SCREENS, ACTOR_ASSIST_SCREENS,
// PERSONAL_SCREENS and a personal-only actor denylist) are now one table in
// routes/route.ts — SCREEN_OWNERS, where the Route union that declares a
// screen is also where the screen names the packs it belongs to. That is the
// refactor the denylist's own comment asked for by name, and it closes the
// holes a pair of claim-sets structurally leaves: a screen no set claims was
// reachable everywhere.

// S7 (Part A) → GAP 57 readiness: which record the Knot pre-associates a
// capture with, by detail screen, now in the BOOTED PACK's own types —
// lib/captureSubject.ts holds the table and the rule.

type AuthState =
  | { phase: 'booting' }
  | { phase: 'verifying-link' }
  | { phase: 'signed-out'; linkError?: string }
  | { phase: 'signed-in'; user: ApiUser };

function displayName(user: ApiUser): string {
  const trimmed = user.name?.trim();
  if (trimmed) return trimmed;
  const [local] = user.email.split('@');
  return local || user.email;
}

// A2.1b: the top-level auth gate. Unauthenticated -> Login; a just-clicked
// magic-link lands here first (verifying-link) before either Login (link
// invalid/expired) or the authenticated shell; authenticated but no active
// workspace yet -> TenantResolver (Auth-2: GET /auth/tenants -> one
// auto-selects, none -> onboarding, many -> picker); otherwise the normal shell.
//
// B1: the shell is now the 5-tab frame (Today · Auditions · [Knot] ·
// Projects · Career) + the universal Strip pattern (see routes/route.ts
// and components/{Strip,NavZone}.tsx for the route model and the reusable
// component itself). The Knot's capture sheet (CaptureSheet) is reachable
// from every screen here, not per-tab — it's rendered once, at this level.
export function App() {
  const [path, rawNavigate] = useHashRoute();

  // ⚑ A3's SUBJECT SCOPE IS DELETED (2026-09-19 ruling: an account is one
  // career, so there is nothing left to narrow between). `navigate` used to
  // be wrapped here so every navigation in this file inherited whichever
  // performer's career the parent was standing in; there is no scope left to
  // inherit, so it is the raw hash navigation now.
  const navigate = rawNavigate;

  const [knotState, setKnotState] = useState<KnotState>('idle');
  const [sheetOpen, setSheetOpen] = useState(false);
  // S12b Part C: the shell's search sheet. Separate state from the capture
  // sheet on purpose — they are two instruments, which is the whole point.
  const [findOpen, setFindOpen] = useState(false);
  // A spoken capture's transcript, carried into the sheet when the intent
  // layer hands it back (unclear, or no intent engine on this instance).
  const [spokenText, setSpokenText] = useState('');
  // S5d §1 — a hold the queue took (offline, or the words rescued from a
  // failed intent step). The sheet opens straight on the receipt for it —
  // the same surface a queued typed capture shows, because it is the same
  // fact: kept on this device, sends itself.
  const [queuedCapture, setQueuedCapture] = useState<{ entry: OutboxEntry; ahead: number } | null>(
    null
  );
  // v1.0.3 — a 'ready' intent the hold lane classified, held for the sheet's
  // see-it-first proposal card instead of created on release.
  const [spokenIntent, setSpokenIntent] = useState<NonNullable<
    Parameters<typeof CaptureSheet>[0]['initialIntent']
  > | null>(null);
  const [auth, setAuth] = useState<AuthState>({ phase: 'booting' });
  const [tenantId, setTenantId] = useState<string | null>(null);
  /** ⚑ IS THIS HOUSEHOLD'S SETUP STILL UNFINISHED? (2026-09-16)
   *
   * 'unknown' until the membership read below answers, 'resume' when the
   * household says setting up never ended, 'clear' otherwise — and 'clear'
   * on ANY trouble, because failing open into the shell is the only
   * direction that cannot trap anybody.
   *
   * Read from the SAME `listTenants()` call the ghost-tenant guard already
   * makes on every signed-in boot, so this costs no request and no extra
   * wait. The shell is NOT held while the answer is outstanding — see the
   * gate further down for why a beat of Today beats a blank app. */
  const [setupGate, setSetupGate] = useState<'unknown' | 'resume' | 'clear'>('unknown');
  // B18b: which SHELL this person gets. The portal answers only a
  // client-role membership and refuses everyone else, so asking it IS the
  // question — no role plumbing, and no second endpoint whose answer could
  // disagree with the one that actually gates the data.
  const [portalMe, setPortalMe] = useState<PortalMeApi | null>(null);
  // The calm, non-blocking "we emailed you a link to confirm your address"
  // note from a fresh password registration — surfaced once, in onboarding.
  const [signupNotice, setSignupNotice] = useState<string | undefined>(undefined);
  // C6b: whether this member still owes their first run — SERVER-held
  // state (the 08-04 SW incident bans the cached kind), asked once per
  // (session, tenant) and only on a FIRST_RUN_VERTICALS build. The initial
  // value is computed SYNCHRONOUSLY off the stored vertical so a build
  // with no first-run script never gates at all — not even for one paint:
  // Actor's boot sequence stays byte-identical (its suites hold the line).
  const [firstRunStatus, setFirstRunStatus] = useState<FirstRunStatus>(() => {
    const vertical = getStoredTenantVertical();
    return vertical && (FIRST_RUN_VERTICALS as readonly string[]).includes(vertical) ? 'unknown' : 'clear';
  });
  // Called unconditionally (rules of hooks) above every early return so the
  // authenticated shell below can pick the bottom-nav vs side-rail rendering.
  const breakpoint = useBreakpoint();

  // CV2 §6 — the tab learns its pack's name. `document.title` comes from
  // the same source the manifest route reads (the pack's entry config,
  // served public by GET /auth/entry — the sign-in screen already consumes
  // it), so the browser tab and the home-screen icon can never disagree
  // about what this deployment is called. index.html's static "Vowric"
  // stays as the pre-answer fallback and the neutral deployment's truth.
  // THE DEPLOYMENT'S OWN PACK, from the server that is running it.
  //
  // A server boots exactly ONE pack, and /auth/entry says which — publicly,
  // with no tenant and no session. The rail used to be chosen from the
  // localStorage vertical alone, which is a property of a TENANT: before the
  // first workspace exists, or while a retired deployment's value is still in
  // the browser, that answer is absent or wrong, and the shell fell back to
  // another vertical's rail. Found live 2026-08-24 — a fresh Rexx sign-in
  // showed the Actor rail. The stored (tenant) vertical still wins when there
  // is one; this is the floor beneath it, never a guess.
  const [deploymentVertical, setDeploymentVertical] = useState<string | null>(null);
  // Whether the deployment-vertical read has SETTLED (answered or failed).
  // A pre-auth door that belongs to one pack (Eden's onboarding link, below)
  // must not decide before the answer is in — neither flash another pack's
  // Login at a personal visitor nor render a personal screen elsewhere.
  const [deploymentVerticalSettled, setDeploymentVerticalSettled] = useState(false);
  // GAP 57 readiness — the pack's declared capture subjects (GET /auth/entry),
  // so the Knot names THIS pack's record type on a detail page.
  const [captureSubjectTypes, setCaptureSubjectTypes] = useState<string[] | null>(null);
  // ⚑ AND THE NAME IS KEPT, not just spent on the tab. Huan, 2026-08-28: a
  // person on CallToWrap the product "should not see Vowric anywhere". The
  // deployment already announces what it is called; the only reason "Vowric"
  // survived on screen is that this answer was read once, written into
  // `document.title`, and thrown away — so every other place that needed the
  // name had a hardcoded one instead. Held in state, it feeds the masthead a
  // reviewer sees and the waiting screens, and a neutral deployment still
  // falls back to Vowric, which on Eden proper IS the product.
  const [productName, setProductName] = useState<string | null>(null);
  // B16 §6 — whether THIS deployment even has a knot intent door. true until
  // the read lands (the door existed on every build before this flag, so a
  // slow answer must not disable it), false only once /auth/entry actually
  // says so — actor_assist declares no knotIntent config, so this settles to
  // false on every CallToWrap build.
  const [knotIntentEnabled, setKnotIntentEnabled] = useState(true);
  useEffect(() => {
    getEntryConfig()
      .then((entry) => {
        if (entry?.productName) {
          document.title = entry.productName;
          setProductName(entry.productName);
        }
      })
      .catch(() => {
        // The static title is already an honest default.
      });
    getDeploymentVertical()
      .then((v) => setDeploymentVertical(v))
      .catch(() => {
        // A pre-packs build answers null; callers keep legacy behaviour.
      })
      .finally(() => setDeploymentVerticalSettled(true));
    getCaptureSubjectTypes()
      .then((types) => setCaptureSubjectTypes(types))
      .catch(() => {
        // Unreadable: shared heads send the id alone (lib/captureSubject.ts).
      });
    getKnotIntentEnabled()
      .then((enabled) => setKnotIntentEnabled(enabled))
      .catch(() => {
        // Unreadable answers as "there is a door" — the pre-existing
        // behaviour (always ask) rather than a guessed refusal.
      });
  }, []);

  // B18b: ask the portal who this is, once a workspace is known. A 200
  // means a client-role membership (the portal refuses everyone else), and
  // that is what chooses the shell. Re-asked on every tenant change, because
  // the same person can be a client in one workspace and not in another.
  //
  // PERSONAL ONLY. `/api/portal/me` is the cockpit's own client portal
  // (portalClient.ts's header: "the cockpit's client, who has no record at
  // all" — a party root plus an engagement) — it has no meaning for a
  // CallToWrap or Rexx tenant, which never mint that identity. Calling it on
  // every workspace meant every actor_assist/rexx load carried a 403 nobody
  // could ever clear, drowning out a refusal that might actually mean
  // something. Same gate shape as the Rexx-only unread-badge effect above.
  useEffect(() => {
    if (!tenantId || getStoredTenantVertical() !== 'personal') { setPortalMe(null); return; }
    let cancelled = false;
    getPortalMe(tenantId)
      .then((me) => {
        if (cancelled) return;
        setPortalMe(me);
        setShellHint(tenantId, 'portal');
      })
      // Any refusal — 403 for "not a client here", or anything else — means
      // the ordinary shell. A portal that cannot answer must never strand
      // somebody on a blank page.
      .catch(() => {
        if (cancelled) return;
        setPortalMe(null);
        setShellHint(tenantId, 'app');
      });
    return () => { cancelled = true; };
  }, [tenantId]);

  useEffect(() => {
    bootstrapTenantFromUrl();
    setTenantId(getStoredTenantId());

    // THE GHOST-TENANT GUARD (found live 2026-08-10, minutes after the first
    // Rexx Box cutover; FOUND AGAIN 2026-08-24, minutes after the second):
    // localStorage's vowric_tenant_id survives ACROSS DEPLOYMENTS on the same
    // domain — a retired build used the same key, so its tenant id (a row in a
    // database this server has never seen) walks into every X-Tenant-Id header
    // and the shell renders nothing but "no active membership in the requested
    // tenant". A stored id is a HINT, never an identity: validate it against
    // the server's own membership list and drop it when it is not ours, so
    // TenantResolver's real flow (auto-select / onboarding / picker) takes
    // over. Client-held state must never be able to trap a person.
    //
    // ⚑ IT MUST RUN ON EVERY SIGNED-IN PATH. The 08-10 version guarded only
    // the plain boot, and both link landings `return` before reaching it — so
    // the one door a returning person uses AFTER a cutover, the emailed link,
    // was the one door with no guard. That is how the identical failure
    // reappeared verbatim fourteen days later. Any new sign-in path calls this.
    //
    // A FAILED validation read keeps the stored id — failing open here means
    // "try the workspace you had", never a lockout.
    //
    // ⚑ IT ALSO ANSWERS "DID THEY EVER FINISH SETTING UP?" (2026-09-16). The
    // membership row carries `setupInProgress`, so the one read this guard
    // already makes settles both questions and the boot pays for neither
    // twice. Only the OWNER is ever resumed: onboarding creates a household,
    // and nobody else's reload should be redirected into one.
    async function dropGhostTenant(): Promise<void> {
      const stored = getStoredTenantId();
      if (!stored) {
        setSetupGate('clear');
        return;
      }
      try {
        const memberships = await listTenants();
        const mine = memberships.find((m) => m.tenantId === stored);
        if (!mine) {
          clearStoredTenantId();
          setTenantId(null);
          setSetupGate('clear');
        } else {
          if (mine.vertical) {
            // Same authoritative read — keep the skin's vertical fresh.
            setStoredTenantVertical(mine.vertical);
          }
          setSetupGate(mine.setupInProgress === true && mine.role === 'owner' ? 'resume' : 'clear');
        }
      } catch {
        // See the header note: validation trouble must not lock out — and an
        // unanswerable setup question means the shell, never a walk nobody
        // asked for.
        setSetupGate('clear');
      }
    }

    async function boot() {
      const isMagicLinkLanding = window.location.pathname === MAGIC_LINK_VERIFY_PATH;
      const isInviteLanding = window.location.pathname === INVITE_ACCEPT_PATH;
      const token = new URLSearchParams(window.location.search).get('token');

      // C6b: the invite landing — "Tim gets a text with a link". The
      // clicked GET serves this shell (staticWeb's carve-out); the token
      // is POSTed here, the invite's own tenant becomes the active one,
      // and the first-run gate below decides what he sees first.
      if (isInviteLanding && token) {
        setAuth({ phase: 'verifying-link' });
        try {
          const { user, tenantId: invitedTenantId } = await acceptInvite(token);
          setStoredTenantId(invitedTenantId);
          try {
            const memberships = await listTenants();
            const mine = memberships.find((t) => t.tenantId === invitedTenantId);
            if (mine) setStoredTenantVertical(mine.vertical);
          } catch {
            // The skin dresses later sessions; missing it must not block
            // the door.
          }
          window.history.replaceState(null, '', '/');
          setTenantId(invitedTenantId);
          setAuth({ phase: 'signed-in', user });
          // The invite named its own tenant, so nothing stale can be in play
          // here — but the guard is cheap and the next path added must not
          // have to remember. It no-ops when the stored id is the invite's.
          await dropGhostTenant();
        } catch (err) {
          window.history.replaceState(null, '', '/');
          // ⚑ THE SERVER'S OWN SENTENCE REACHES THE READER (2026-09-16).
          //
          // This `catch` used to take no binding at all, so every refusal the
          // accept route can give arrived here as the same dead-end line. The
          // one that mattered most was the 409 `member_age_required` — "This
          // account records every member's date of birth before their seat
          // goes live. Ask the account owner to add it, then open this
          // invitation again." — a FIXABLE state, thrown away and shown as a
          // dead token. A person told their link is expired asks for a new
          // link; a person told the truth asks the owner for a birth date.
          // The refusal that cannot be told apart from a bug is the voice
          // canon's own test, and this failed it.
          //
          // `serverMessage` is set only where the body carried a written
          // sentence, so a 500, a proxy's HTML or a bare `{error:"..."}` code
          // still gets our own words rather than "request failed".
          setSetupGate('clear');
          setAuth({
            phase: 'signed-out',
            linkError:
              (err instanceof ApiError ? err.serverMessage : undefined) ??
              'This invite link is invalid or has expired.',
          });
        }
        return;
      }

      if (isMagicLinkLanding && token) {
        setAuth({ phase: 'verifying-link' });
        try {
          const { user } = await verifyMagicLink(token);
          window.history.replaceState(null, '', '/');
          setAuth({ phase: 'signed-in', user });
          await dropGhostTenant();
        } catch {
          window.history.replaceState(null, '', '/');
          setSetupGate('clear');
          setAuth({ phase: 'signed-out', linkError: 'This link is invalid or has expired.' });
        }
        return;
      }

      const user = await whoami();
      setAuth(user ? { phase: 'signed-in', user } : { phase: 'signed-out' });
      // ⚠ EVERY PATH MUST LEAVE THE SETUP GATE ANSWERED. It holds the screen
      // on 'unknown', so a branch that never resolves it is a blank app — the
      // exact class of trap this file keeps closing.
      if (user) await dropGhostTenant();
      else setSetupGate('clear');
    }

    void boot();
  }, []);

  // C6b: the first-run gate's one question, asked when a signed-in session
  // holds a tenant on a first-run-capable vertical. Anything but a clear
  // "incomplete walk" answer — no script on this build (404), already
  // complete, network trouble — fails OPEN into the shell: the server
  // holds the state precisely so the client can never trap anyone.
  useEffect(() => {
    if (auth.phase !== 'signed-in' || !tenantId) return;
    const vertical = getStoredTenantVertical();
    if (!vertical || !(FIRST_RUN_VERTICALS as readonly string[]).includes(vertical)) {
      setFirstRunStatus('clear');
      return;
    }
    let cancelled = false;
    setFirstRunStatus('checking');
    getFirstRun(tenantId)
      .then((state) => {
        if (cancelled) return;
        setFirstRunStatus(!state.completedAt && state.resume !== null ? 'pending' : 'clear');
      })
      .catch(() => {
        if (!cancelled) setFirstRunStatus('clear');
      });
    return () => {
      cancelled = true;
    };
  }, [auth.phase, tenantId]);

  /* ---------------------------------------------------------------- *
   * CV6b §4 — THE SHELL'S OWN UNREAD READ, and the phone's channel.
   *
   * The Chat TAB carries the badge, and the shell renders that tab on every
   * screen — including the ones that never open a conversation list. So the
   * shell reads the list once itself rather than inheriting a number from a
   * screen the person may never visit. One page, one request, Rexx only
   * (the route 404s on a pack with no comms config), and only once the
   * first-run walk is out of the way.
   *
   * `unreadNonce` is how a push re-reads it: the worker tells this window a
   * thread moved (lib/swMessages), and the badge catches up without a
   * reload — which is the acceptance, not a nicety.
   * ---------------------------------------------------------------- */
  const [unreadNonce, setUnreadNonce] = useState(0);
  const unread = useUnreadTotal();
  useEffect(() => {
    if (auth.phase !== 'signed-in' || !tenantId || firstRunStatus !== 'clear') return;
    if (getStoredTenantVertical() !== 'rexx') return;
    let cancelled = false;
    listCommsConversations(tenantId, { limit: 50, offset: 0 })
      .then((page) => {
        if (cancelled) return;
        publishThreadUnread(
          page.map((c) => ({
            key: c.kind === 'job' ? threadKey('job', c.recordId) : threadKey(c.lane, c.membershipId),
            count: c.unreadCount,
            capped: c.unreadCountCapped,
          }))
        );
      })
      .catch(() => {
        // A refusal or an offline boot costs the BADGE, never the app. The
        // screens read the same list for themselves and say their own
        // failure out loud when it is theirs to say.
      });
    return () => {
      cancelled = true;
    };
  }, [auth.phase, tenantId, firstRunStatus, unreadNonce]);

  /* CV6b §2 — RE-REGISTER ON EVERY BOOT WHEN PERMISSION IS ALREADY GRANTED.
   * Asks the human for nothing and does nothing unless a subscription
   * already exists. A push endpoint the browser has rotated and nobody
   * re-posts is a phone that has silently stopped buzzing and looks fine. */
  useEffect(() => {
    if (auth.phase !== 'signed-in' || !tenantId || firstRunStatus !== 'clear') return;
    void reregisterPushOnBoot(tenantId);
  }, [auth.phase, tenantId, firstRunStatus]);

  /* CV6b §1 — THE WORKER SPEAKS TO THIS WINDOW.
   * 'navigate' is a clicked notification: the worker never navigates a
   * window itself (an installed window has no way back — CV5 §1), it hands
   * the app the hash and the app moves. 'refresh' is a push that
   * deliberately did not buzz because this thread is open and visible; the
   * thread itself reloads (ChatThread listens too) and the badge re-reads
   * here. */
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  useEffect(
    () =>
      onServiceWorkerPushMessage((message) => {
        if (message.verb === 'navigate') navigateRef.current(message.path);
        setUnreadNonce((n) => n + 1);
      }),
    []
  );

  // S1 (decision d764dd89) wired ⌘K to the Knot's capture sheet, on the
  // reading that the Knot and the command palette are one instrument.
  //
  // S12b UNWIRES THAT HALF, on Huan's 2026-08-12 ruling. The premise was
  // that the resolver behind the sheet could also FIND; it cannot — the
  // Knot's declared intents are CREATE targets, so "⌘K to find a client"
  // filed a note. A shortcut that reaches the wrong instrument is worse
  // than no shortcut, and the fix is not to teach the classifier to search
  // (search is its own instrument until the Knot can actually find things)
  // but to point the key at the search that now exists.
  //
  // The Knot keeps the sheet: its own press, its own hold, unchanged.
  // ⌘K is now the ACCELERATOR for FindEntry — never the only door to it
  // (wf01 A3, and Huan's "how about on a phone?").
  //
  // Rexx-only, deliberately: the find sheet reads Rexx registries, so on
  // Actor and Personal the shortcut keeps its pre-S12b behaviour exactly.
  useEffect(() => {
    if (auth.phase !== 'signed-in' || !tenantId) return;
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (getStoredTenantVertical() === 'rexx') {
          setFindOpen(true);
          return;
        }
        setSpokenText('');
        setKnotState('listening');
        setSheetOpen(true);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [auth.phase, tenantId]);

  // B7 — the EXTERNAL reviewer portal. Detected BEFORE the auth/tenant gate:
  // it's for a visitor with NO session and NO tenant, reached by the hash route
  // #/portal/review/:token (the human-facing "send-to-coach" link). The token is
  // the ONLY credential — the portal fetches its own scoped context, tape, and
  // submit against /portal/review/:token (all token-authorized server-side).
  // ⚑ THE COACH'S PUBLISHED HANDLE — `/c/<slug>`, A REAL PATH (rung 4,
  // decision `828b8687`). The ONLY screen in this product not on the hash, and
  // deliberately so: a coach prints this URL, so a `#` in it is a liability.
  // Read from `window.location.pathname` exactly as the magic-link and invite
  // landings already are, and served because `/c` is not a reserved prefix, so
  // it falls through staticWeb's catch-all to this shell.
  //
  // ⚠ IT SITS HERE, WITH THE PORTALS, AND THE POSITION IS THE FEATURE. A
  // STRANGER WITH NO ACCOUNT IS THE WHOLE AUDIENCE — they are the family the
  // coach is recruiting. Placed after the signed-out gate (as the first draft
  // had it) every such visitor is shown Login instead, which defeats the
  // entire growth loop this rung exists for. It grants nothing and asks for
  // nothing, so there is nothing to gate.
  const intakeMatch = window.location.pathname.match(/^\/c\/([^/]+)\/?$/);
  if (intakeMatch) {
    return (
      <CoachIntakeLanding
        slug={decodeURIComponent(intakeMatch[1]!)}
        signedIn={auth.phase === 'signed-in'}
        brand={productName}
      />
    );
  }

  const portalMatch = path.match(/^\/portal\/review\/([^/]+)$/);
  if (portalMatch) {
    return <ReviewerPortal token={decodeURIComponent(portalMatch[1]!)} brand={productName} />;
  }

  // B1 — the invite screen (`/#/beta/<token>`). Same placement as the two
  // portal routes just above and for the identical reason: a stranger with
  // no account is the WHOLE audience here (B1 §4), so this must be checked
  // before the signed-out gate would otherwise hand them Login.
  const betaInviteMatch = path.match(/^\/beta\/([^/]+)$/);
  if (betaInviteMatch) {
    return <BetaInvite token={decodeURIComponent(betaInviteMatch[1]!)} />;
  }

  // Core hardening 2026-07-29, Item 9: the GENERIC sibling — any
  // 'record'-target action-link grant (approve/sign/rate/upload), not just
  // the review flow above. Deliberately a distinct hash namespace
  // (#/portal/:token, not #/portal/review/:token) so this never shadows or
  // gets shadowed by the review route.
  const recordGrantMatch = path.match(/^\/portal\/([^/]+)$/);
  if (recordGrantMatch) {
    return <RecordGrantPortal token={decodeURIComponent(recordGrantMatch[1]!)} brand={productName} />;
  }

  // V19b-2 — Eden's client onboarding surface (docs/console/
  // v19b-the-client-surface.md). Same posture as B7's own comment just
  // above: detected BEFORE the auth/tenant gate, for a visitor with NO
  // session and NO tenant. "The link is the verification" (`89f02609`) —
  // the token in the hash route IS the credential; the screen fetches its
  // own scoped context, all token-authorized server-side, exactly the
  // ReviewerPortal pattern this chunk was told to reuse rather than
  // rebuild.
  //
  // ⚑ THE COCKPIT'S ALONE (GAP 57 readiness, 2026-09-29). This matched by
  // path before any pack check, so a CallToWrap or Rexx build rendered Eden's
  // client onboarding to anyone who typed the hash. It is `personal`'s door:
  // on any other deployment the path falls through exactly as an unknown path
  // does (Login signed out, Today signed in). Until the deployment's vertical
  // has been read, nothing is decided — the boot placeholder holds.
  const edenOnboardingMatch = path.match(/^\/eden\/onboarding\/([^/]+)$/);
  if (edenOnboardingMatch && !deploymentVerticalSettled) {
    return (
      <div class="app-shell">
        <PlaceholderScreen eyebrow={productName ?? ''} line="One moment…" />
      </div>
    );
  }
  if (edenOnboardingMatch && packVerticalOf(deploymentVertical) === 'personal') {
    return <EdenOnboarding token={decodeURIComponent(edenOnboardingMatch[1]!)} brand={productName} />;
  }

  // Dev-only design-review route — no shell/nav chrome around it, it's a
  // gallery of the primitives themselves, not a product screen. Available
  // regardless of auth state (it renders no live data).
  //
  // ⚑ AND DEV-ONLY IS NOW ENFORCED, NOT DESCRIBED (2026-09-01). "Dev-only"
  // was the comment's word and nothing in the code held it: a production
  // build served this gallery to anybody who typed the hash, SIGNED OUT, and
  // the specimens in it carry another customer's name. In a production build
  // the hash is unrecognised and lands on Today, exactly as any other unknown
  // path does.
  if (import.meta.env.DEV && path === '/_kit') {
    return <Kit />;
  }

  if (auth.phase === 'booting' || auth.phase === 'verifying-link') {
    return (
      <div class="app-shell">
        <PlaceholderScreen
          eyebrow={productName ?? ''}
          line={auth.phase === 'verifying-link' ? 'Signing you in…' : 'One moment…'}
        />
      </div>
    );
  }

  if (auth.phase === 'signed-out') {
    return (
      <div class="app-shell">
        <Login
          initialError={auth.linkError}
          onAuthenticated={(user, opts) => {
            setSignupNotice(
              opts?.justRegistered
                ? 'We emailed you a link to confirm your address — no need to act on it now.'
                : undefined
            );
            // Signing in HERE does not re-run boot's membership read, so the
            // gate is answered the only honest way an unasked question can be:
            // 'clear'. The next boot asks properly. The window this leaves is
            // one load wide, and the address it guards stays changeable until
            // the household's first performer exists (mailbox.ts) — so nothing
            // is settled behind anyone in the meantime.
            setSetupGate('clear');
            setAuth({ phase: 'signed-in', user });
          }}
        />
      </div>
    );
  }

  // THE COACH SURFACE (rung 1) — reached at #/pro.
  //
  // Placed HERE, and the position is load-bearing in both directions:
  //
  //   * AFTER the signed-out gate above, because unlike the token portal
  //     this door is NOT anonymous — a coach signs in. A signed-out visitor
  //     therefore gets Login, and because the hash survives that round trip
  //     they land back here once authenticated.
  //   * BEFORE the tenant gate below, because TenantResolver would push a
  //     coach to CREATE A HOUSEHOLD. A coach belongs to no household and
  //     must never acquire a membership — that is the single property the
  //     whole surface rests on (spec §3), and the tenant gate is the one
  //     place in this app that would quietly violate it.
  //
  // Sub-paths (#/pro/students/:label, #/pro/report) arrive in rungs 3 and 5;
  // the prefix match is deliberate so they land on the coach shell rather
  // than falling through to the tenant app in the meantime.
  // Returned WITHOUT the `.app-shell` wrapper, like the two portals above:
  // that wrapper is a 100dvh flex container built for the tenant chrome (rail,
  // tabs, subnav), none of which a coach has. CoachShell owns its own
  // scrolling root instead — see CoachShell.css.
  if (path === '/pro' || path.startsWith('/pro/')) {
    return <CoachShell brand={productName} />;
  }

  // ⚑ SETTING UP NEVER ENDED — SO FINISH IT, RATHER THAN PRETENDING IT DID
  // (2026-09-16). Placed ABOVE the tenant gate because the household already
  // exists: a reload part-way through onboarding has a stored tenant id, the
  // resolver auto-selects it, and the remaining questions — the address the
  // family may still name, the passkey offer — were simply never asked again.
  // The address one is not recoverable: since 2026-09-15 it freezes the moment
  // the first performer exists, so the derived default a reload accepted on
  // the family's behalf becomes theirs forever.
  //
  // ⚠ IT DOES NOT HOLD THE SCREEN WHILE IT ASKS, and that is deliberate. The
  // first draft rendered "One moment…" until the membership read answered —
  // which turns a slow or unanswerable read into a blank app for EVERYBODY, the
  // exact lockout the ghost-tenant guard above refuses to risk ("failing open
  // means try the workspace you had, never a lockout"). So the shell paints as
  // it always did, and an interrupted setup sees one beat of Today before the
  // question it was on comes back. A flash costs a family a moment; a hold
  // costs them the app.
  if (auth.phase === 'signed-in' && tenantId && setupGate === 'resume') {
    return (
      <div class="app-shell">
        <Onboarding
          user={auth.user}
          resumeTenantId={tenantId}
          onResolved={(id) => {
            setSetupGate('clear');
            setTenantId(id);
          }}
          onUserUpdated={(u) => setAuth({ phase: 'signed-in', user: u })}
        />
      </div>
    );
  }

  if (!tenantId) {
    return (
      <div class="app-shell">
        <TenantResolver
          user={auth.user}
          notice={signupNotice}
          onResolved={setTenantId}
          // The name onboarding just saved, so Today greets them by it rather
          // than by their email handle (2026-09-15) — the same hand-back
          // Settings has always made.
          onUserUpdated={(u) => setAuth({ phase: 'signed-in', user: u })}
        />
      </div>
    );
  }

  // B18b: a CLIENT gets the portal, never the cockpit shell — without this
  // a client-role login lands in the personal pack's tabs, where every
  // request 403s (the role floor holds — see portalClient.ts) but the
  // surface still shows them the names of Huan's own screens.
  //
  // The REMEMBERED hint decides what to do while the server is being asked
  // (lib/tenant.ts says why): a known client waits a beat rather than
  // seeing a cockpit flash past, and everybody else — Huan, every cold
  // load — renders immediately and never pays for a case that is not
  // theirs.
  if (portalMe) {
    return (
      <div class="app-shell">
        <ClientPortal tenantId={tenantId} me={portalMe} />
      </div>
    );
  }
  if (getShellHint(tenantId) === 'portal') {
    return (
      <div class="app-shell">
        <PlaceholderScreen eyebrow={productName ?? ''} line="One moment…" />
      </div>
    );
  }

  // C6b: the first-run gate. While the server is being asked, the same
  // quiet beat as booting (never a flash of the wrong screen); an
  // incomplete walk renders the walk itself. Both resolve within one
  // request, and only ever on a FIRST_RUN_VERTICALS build (see the gate
  // effect above) — Actor/Personal skip straight through.
  if (firstRunStatus === 'unknown' || firstRunStatus === 'checking') {
    return (
      <div class="app-shell">
        <PlaceholderScreen eyebrow={productName ?? ''} line="One moment…" />
      </div>
    );
  }
  if (firstRunStatus === 'pending') {
    return (
      <div class="app-shell">
        <FirstRun tenantId={tenantId} user={auth.user} onDone={() => setFirstRunStatus('clear')} />
      </div>
    );
  }

  // R2 dev-only harness — proves the schema-driven record-operations engine
  // (create/edit/delete/transition) end to end through the real records API.
  // Rendered here (after auth + tenant are resolved) so it has a live
  // tenantId; no shell/nav chrome (like /_kit). R3 wires RecordForm/
  // RecordActions into the actual product screens.
  //
  // ⚑ DEV-ONLY, ENFORCED (2026-09-01) — the same fix as `/_kit` above and a
  // sharper reason: this harness is raw create/edit/delete over every record
  // type the pack declares, and it was reachable by ANY signed-in member of a
  // tenant, a child's login included. A production build carries no door into
  // the record engine.
  if (import.meta.env.DEV && (path === '/_records' || path.startsWith('/_records/'))) {
    return (
      <div class="app-shell">
        <RecordsHarness tenantId={tenantId} path={path} navigate={navigate} />
      </div>
    );
  }

  const userName = displayName(auth.user);
  // ⚑ ONE GATE, DERIVED FROM THE OWNER REGISTRY (routes/route.ts's
  // SCREEN_OWNERS). A screen renders iff the booted pack is one of its
  // declared owners; everything else falls to Today, which is the same
  // fallback parseRoute already gives an unrecognised path.
  //
  // MERGE (2026-08-26, lane-a × trunk), still true and still load-bearing:
  //   Trunk (Rexx merge): `effectiveVertical` — tenant first, deployment as
  //     the floor, so a single-tenant deployment knows what it is before a
  //     tenant is picked. ALL vertical flags derive from it.
  //   Lane A: `path` used to carry a subject-scope prefix that parseRoute had
  //     to be shielded from (`barePath`); the scope is deleted (2026-09-19
  //     ruling), so parseRoute reads `path` directly like every other
  //     vertical always has.
  //
  // `effectiveVertical` stays the RAW string because the operator skin reads
  // it and skins a vertical this router has no screens for (`workshop`);
  // `packVertical` is the routed identity, which is a narrower question.
  const effectiveVertical = getStoredTenantVertical() ?? deploymentVertical;
  const packVertical = packVerticalOf(effectiveVertical);
  const isRexx = packVertical === 'rexx';
  const isPersonal = packVertical === 'personal';
  const isActorAssist = packVertical === 'actor_assist';
  // Build 1 of the 01B nav: on the cockpit, the old Calendar/To-dos/People
  // addresses land inside the Personal tab (route.ts foldIntoPersonal).
  let parsed = isPersonal ? foldIntoPersonal(parseRoute(path)) : parseRoute(path);
  // H1a (COCKPIT-HOME-COLUMNS-SPEC.md §H1) — the personal pack's DEFAULT
  // route becomes `#/home`; `#/today` stays reachable and untouched. Only
  // the bare default (`path === '/'`, parseRoute's own fallback for "no
  // path at all") redirects — a hand-typed `#/today`, or any other
  // explicit address that happens to fall back to `today` (route.ts's
  // existing "unowned screen" fallback, unaffected), is never rewritten
  // here. Every other pack's default stays `/` → Today, byte-identical.
  if (isPersonal && path === '/' && parsed.screen === 'today') {
    parsed = { screen: 'home' };
  }
  const route: Route = screenOwnedBy(parsed.screen, packVertical) ? parsed : { screen: 'today' };
  // A3 — `materials` IS TWO THINGS on an actor_assist build; see the
  // pre-merge comment history. The rail lights the tab the pack declares.
  const activeTab = isActorAssist && route.screen === 'materials' ? 'materials' : topTab(route);

  // S1c (decision eb659c8d) — THE OLD PERSONAL › ACCOUNTS ADDRESS
  // REDIRECTS, NEVER 404s. Google connections moved to Settings ›
  // Connected accounts; this is the ONLY current producer of
  // `{screen:'personal', sub:'accounts'}` (Mail's reconnect link now
  // targets Settings directly — see the Mail render case), so redirecting
  // every arrival at this exact shape is safe: nothing else in this file
  // navigates here on purpose. `replace` (useHashRoute's own
  // normalization convention) so the back button doesn't loop between the
  // retired address and the new one. `route.state` (the OAuth outcome) is
  // NOT dropped: it's forwarded verbatim as the new route's own `state` —
  // see routes/route.ts's settings Route member and App.tsx's Settings
  // render case, which reads it as `connectedAccountsOutcome`.
  //
  // NOT done for `{screen:'threads'}` (the old Conversations landing):
  // that exact Route shape is still produced by live internal
  // navigation — the thread filter chips and threadDetail's own
  // back-target (see the 'threads' case below and `parentRoute`) — so a
  // blanket redirect on the shape alone would break them. The Threads
  // screen stays fully routable (SCREEN_OWNERS, unchanged); only the
  // SWITCHER no longer offers it as a door (cockpitSurfaces.ts has no
  // 'threads' entry; Mail is its replacement).
  useEffect(() => {
    if (isPersonal && route.screen === 'personal' && route.sub === 'accounts') {
      navigate(routePath({ screen: 'settings', state: route.state }), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPersonal, route.screen, route.screen === 'personal' ? route.sub : undefined, route.screen === 'personal' ? route.state : undefined]);

  function pressKnot() {
    // R5b: pressing the Knot opens the capture sheet AND starts the Knot
    // listening. The sheet drives the remaining beats (understanding ->
    // proposing -> confirmed) via onKnotState as the capture loop runs; a real
    // microphone amplitude feeding the listening state is a LATER piece (the
    // Knot synthesises a lively amp itself for now).
    // A tap is a fresh capture — never a reopened receipt from a hold.
    setQueuedCapture(null);
    setKnotState('listening');
    setSheetOpen(true);
  }

  function closeSheet() {
    setSheetOpen(false);
    setQueuedCapture(null);
    setSpokenIntent(null);
    setKnotState('idle');
  }

  // S7 / S5d: the current page's subject, derived from the route BEFORE the
  // push-to-talk hook below, because the hold's queue path enqueues with the
  // same context envelope the typed sheet would send (see
  // lib/captureSubject.ts for the table itself).
  const captureSubjectType = captureSubjectTypeFor(route.screen, captureSubjectTypes);
  const captureContext: CaptureContext =
    screenHasCaptureSubject(route.screen) && 'id' in route
      ? captureSubjectType !== undefined
        ? { route: routePath(route), subjectType: captureSubjectType, subjectId: route.id }
        : // A shared head whose pack type is not (yet) known: the id alone —
          // the server resolves the subject from the record's own type.
          { route: routePath(route), subjectId: route.id }
      : { route: routePath(route) };

  // CV3 §1 drop 2 — the voice/intent lane carries the SAME envelope. The
  // comment this replaces said "this shell has no per-screen context
  // envelope yet"; that stopped being true at S7, and passing null here was
  // the leak — on a Rexx build /api/knot/intent got no contextType on ANY
  // screen, so the resolver never saw the job under the tech's feet and
  // knotIntentCreate never linked the created note. The context is the same
  // table-derived subject the typed sheet sends (lib/captureSubject.ts
  // above); a screen with no subject still passes null, which is the locked
  // Knot spec's thin-envelope path, unchanged. On a build with no intent
  // engine (Actor, Personal) the route 404s exactly as it did with null —
  // the hook's own unclear fallback owns that case.
  const knotContext =
    captureSubjectType !== undefined && 'id' in route && typeof route.id === 'string'
      ? { type: captureSubjectType, id: route.id }
      : null;

  // Press-and-hold = the voice gesture (Huan's own direction, quoted in
  // lib/useKnotPushToTalk's header: held listens until release, a quick tap
  // opens the typed sheet). That hook was written and tested in Core hardening
  // but never actually WIRED to a button — the Knot carried only onClick, so a
  // hold did nothing and the phone's own long-press gesture answered instead.
  // This is that wiring.
  const ptt = useKnotPushToTalk(
    tenantId,
    knotContext,
    // Quick tap: unchanged behaviour — open the capture sheet, blank.
    () => {
      setSpokenText('');
      pressKnot();
    },
    // Unclear (or no intent engine on this instance): the sheet opens seeded
    // with the verbatim transcript, so what was said is edited rather than
    // lost.
    (transcript: string) => {
      setSpokenText(transcript);
      pressKnot();
    },
    // CV5 §3 — THE SCREEN HEARS ABOUT A SPOKEN NOTE. This argument was
    // `undefined`, so the hold→intent lane's only success signal fired into
    // nothing: a note spoken on the job screen was written to the server and
    // then invisible until the screen happened to remount. Every OTHER write
    // path in this app already bumps (CaptureSheet, the outbox drain,
    // RecordEditor, WorkOrderDrawer, TeamDoor) — the read surfaces subscribe
    // to this one counter through useRecordsVersion. Wiring it is the whole
    // fix; nothing new is built.
    () => bumpRecordsVersion(),
    undefined,
    // S5d §1 — the queue, wired. Offline (or a send that never landed) the
    // hold's audio goes to the outbox with THIS page's context envelope, and
    // the sheet opens straight on the receipt — a receipt state, not an
    // error. The object is recreated per render so getContext always answers
    // with the current screen; the hook refs it, so the async release reads
    // the latest.
    {
      getContext: () => captureContext,
      onQueued: (entry, ahead) => {
        setSpokenText('');
        setQueuedCapture({ entry, ahead });
        setSheetOpen(true);
      },
    },
    // v1.0.3 (Huan, 2026-08-30): a clear spoken create is PROPOSED, not
    // committed on release — the sheet opens on the card showing what is
    // about to be made, and Approve there is the one gesture that creates.
    (intent, voice) => {
      setSpokenText('');
      setQueuedCapture(null);
      setSpokenIntent({ intent, voice });
      setSheetOpen(true);
    },
    // R-10 — a spoken UPDATE takes the same door: the sheet opens on the
    // diff card (or the which-record dialog), recording ref carried.
    (intent, voice) => {
      setSpokenText('');
      setQueuedCapture(null);
      setSpokenIntent({ intent, voice });
      setSheetOpen(true);
    },
    // B16 §6 — false on a deployment whose pack declares no knotIntent
    // config (CallToWrap today): the hold goes straight to the transcript
    // fallback above instead of spending a round trip on a door that 404s.
    knotIntentEnabled
  );

  // The hook owns the Knot's state only while it's actually driving a voice
  // capture; the capture sheet keeps driving it the rest of the time (it sets
  // knotState through onKnotState). Idle from the hook means "not my turn".
  const shownKnotState = ptt.knotState === 'idle' ? knotState : ptt.knotState;

  // R1a: the bottom nav and the side rail read the SAME nav model (see
  // components/navModel.ts) — only the layout differs by breakpoint.
  // Core hardening 2026-07-29, Item 8: destinations resolved per-session
  // via useNavModel (a pack-declared nav-role tab set, or — for the one pack
  // that declares none, the July Actor one — its own four tabs).
  //
  // ⚑ THE PACK IS PASSED IN (2026-09-01) because the fallback is a claim
  // about a pack, not about the app: an unresolved rail is EMPTY now, and a
  // failed one is empty and says the server's own sentence. CallToWrap used
  // to open on Today · Auditions · Projects · Career every single load.
  //
  // S5a moved this read ABOVE the screen switch (same position relative to
  // every early return, so the hook order is unchanged) because the
  // resolved nav ROLE now decides two things the switch needs: which Today
  // a Rexx member lands on, and which frame the shell wears.
  // The phone gets the landing role's declared bar when the pack names one
  // (Huan, 2026-08-25 — the owner union was eleven destinations wide on a
  // phone). `isRail` is the desktop side rail, so !isRail IS the phone.
  // `overflow` is what the phone bar leaves out — derived from the same
  // union, so More can never disagree with the bar. On the rail it is empty
  // and nothing downstream changes.
  const nav = useNavModel(tenantId, !isRail(breakpoint), packVertical);
  const { destinations, overflow } = nav;
  // Huan's ruling on build 1 of the 01B nav (2026-10-08, "Review into
  // More"): on the cockpit's PHONE, Review sits behind More, and its pending
  // count — the review queue's own badge number, GET /api/proposals/count —
  // rides the More slot so a waiting proposal is still seen from the bar.
  // Re-read on every navigation (approving one is a navigation away from
  // it). Phone only: the desktop rail is unchanged by this ruling.
  const [reviewPending, setReviewPending] = useState(0);
  const phoneCockpit = isPersonal && !isRail(breakpoint);
  // S1c — THESE TWO COUNTS NOW FEED THE TITLE SWITCHER TOO, so they're read
  // on `isPersonal` (any breakpoint), not `phoneCockpit` alone: the switcher
  // replaces the rail on the DESKTOP layout as well, and a count the
  // desktop rail never showed before this chunk is still a count the
  // switcher's own menu items and attention dot need, on both layouts, for
  // the same reason More's badge always needed it on the phone.
  useEffect(() => {
    if (!isPersonal || !tenantId) {
      setReviewPending(0);
      return;
    }
    let cancelled = false;
    countPendingProposals(tenantId).then(
      (n) => {
        if (!cancelled) setReviewPending(n);
      },
      () => {
        // A side read: without it the switcher/More shows no count, and the
        // Review screen itself still reports its own read loudly.
        if (!cancelled) setReviewPending(0);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [isPersonal, tenantId, path]);
  // The Audience tab's own More badge, same posture as reviewPending just
  // above: on the cockpit's PHONE, Audience sits behind More (the rail gets
  // a first-class tab; the phone's four slots do not), so its unanswered
  // count rides the More slot instead — the contract's own
  // `tiles.unanswered`. Re-read on every navigation; a failed side read
  // shows no count rather than an error card. S1c: read on `isPersonal`,
  // same reasoning as reviewPending above.
  const [audienceUnanswered, setAudienceUnanswered] = useState(0);
  useEffect(() => {
    if (!isPersonal || !tenantId) {
      setAudienceUnanswered(0);
      return;
    }
    let cancelled = false;
    getAudienceSummary(tenantId).then(
      (s) => {
        if (!cancelled) setAudienceUnanswered(s.tiles.unanswered);
      },
      () => {
        if (!cancelled) setAudienceUnanswered(0);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [isPersonal, tenantId, path]);
  const [moreOpen, setMoreOpen] = useState(false);
  // MAIL-POLISH BRIEF addition (Huan, final, 2026-10-16) — the cockpit's "+"
  // is CONTEXTUAL: on Mail it opens Compose, on Calendar it opens New event,
  // everywhere else it still opens the global create menu. The button lives
  // in App's own top bar, outside Mail/CockpitCalendar's own trees, so the
  // tap is relayed down as a bumped token (never a boolean — two taps in a
  // row while the sheet is already open from the first must still register
  // as a second request once it's closed) rather than a ref or a second,
  // independently-invented open/close channel.
  const [composeRequestToken, setComposeRequestToken] = useState(0);
  const [newEventRequestToken, setNewEventRequestToken] = useState(0);
  // B8 — "More → Your page" (Huan). Not one of the pack's declared
  // NavDestinations (it has no rail slot — see route.ts's own comment on
  // `yourPage`), so it rides as MoreSheet's own trailing extra entry rather
  // than teaching useNavModel a destination with no tab. actor_assist only:
  // no other pack has a performer to build a page for.
  const moreExtra = isActorAssist
    ? [
        {
          key: 'yourPage',
          label: 'Your page',
          href: `#${routePath({ screen: 'yourPage' })}`,
          onNavigate: () => navigate(routePath({ screen: 'yourPage' })),
        },
      ]
    : [];
  // A sheet that survived the navigation it caused would sit over the screen
  // it just opened. Route changes close it.
  useEffect(() => {
    setMoreOpen(false);
  }, [path]);

  // S5a §1 — A TECH DOES NOT GET THE OFFICE FRAME.
  //
  // "Role decides the frame, viewport decides the sizing." A member whose
  // pack-resolved nav role is `tech` gets wf03's bottom tab bar on any
  // screen size, and a dispatcher on a phone still gets the dispatcher's
  // surfaces — because the tech's screens are the right ones for their job
  // whatever they are holding, and the rail is not something to hide with
  // CSS at a width.
  //
  // The role is READ from what the server resolved (navModel's
  // primaryNavRole — the landing group's own key), never re-declared here:
  // if the pack and the shell disagreed about a tech's tabs, the pack is
  // right, and the way to keep that true is to not hold a second opinion.
  const techFrame = isRexx && primaryNavRole(destinations) === 'tech';

  let screen;
  let strip;

  /** A3 — the registry for one of the pack's tabs, or null on a build whose
   * pack does not claim it. Used by the four heads that are new AND by the
   * four whose tab key an earlier pack already used (auditions, projects,
   * money, materials): the key is shared, the screen behind it is not, and
   * the branch below is where that is decided rather than inside a screen
   * that would then have to know which pack it is running under. */
  const householdRegistry = (tab: string) => {
    const def = householdTab(tab);
    if (!def) return null;
    return (
      <Registry
        tenantId={tenantId}
        eyebrow={def.eyebrow}
        sections={def.sections}
      />
    );
  };

  switch (route.screen) {
    // H1a (COCKPIT-HOME-COLUMNS-SPEC.md §H1) — the cockpit's front page.
    // Personal-only (SCREEN_OWNERS), so this case never fires on any other
    // pack's build.
    case 'home':
      screen = <CockpitHome tenantId={tenantId} onNavigate={(path) => navigate(path)} />;
      strip = undefined;
      break;
    // H3a (COCKPIT-HOME-COLUMNS-SPEC.md §H3) — one project's own screen.
    // Personal-only (SCREEN_OWNERS); parseRoute already refuses any slug
    // but the four known ones, so `route.slug` is always real here.
    case 'projectScreen':
      screen = <ProjectScreen tenantId={tenantId} slug={route.slug} onNavigate={(path) => navigate(path)} />;
      strip = undefined;
      break;
    case 'auditions':
      // A3: this pack's auditions are aa_audition; the screen below queries
      // actor_audition and is a DIFFERENT NAMESPACE with zero overlap (the
      // A2 spec's own finding — 4,146 lines of the July Actor UI are dead
      // code against this pack). No Strip either: AUDITION_STAGES is the old
      // pack's stage vocabulary, and offering it here would filter on states
      // that do not exist.
      // A4: this pack's auditions now have their own list, and its rows open
      // the SPREAD rather than the schema-driven editor. The stage strip is
      // built inside that screen from the fetched lifecycle — AUDITION_STAGES
      // below is the OLD pack's vocabulary and would filter on states
      // aa_audition does not declare.
      if (isActorAssist) {
        screen = (
          <HouseholdAuditions
            tenantId={tenantId}
            onOpen={(id) => navigate(routePath({ screen: 'auditionDetail', id }))}
            onOpenSubmission={(id) => navigate(routePath({ screen: 'submissionDetail', id }))}
            // B21 — an old `/submissions` link or `#/auditions/submissions`
            // lands with that chip selected; every other stage still lands
            // on "All" (findId's own first-option fallback already handles
            // every other unrecognised sub-segment upstream in parseRoute).
            initialFilter={route.stage === 'submissions' ? 'submissions' : 'all'}
          />
        );
        strip = undefined;
        break;
      }
      screen = (
        <Auditions
          tenantId={tenantId}
          stage={route.stage}
          onOpen={(id) => navigate(routePath({ screen: 'auditionDetail', id }))}
        />
      );
      strip = (
        <Strip
          items={AUDITION_STAGES}
          activeId={route.stage}
          onSelect={(stage) => navigate(routePath({ screen: 'auditions', stage }))}
        />
      );
      break;
    case 'auditionDetail':
      // A4: same route, different pack, different screen. aa_audition and
      // actor_audition are different namespaces with zero overlap, so the
      // gate is here rather than inside a screen that would then have to know
      // which pack it is running under.
      if (isActorAssist) {
        screen = (
          <AuditionSpread
            tenantId={tenantId}
            id={route.id}
            onOpenRole={(id) => navigate(routePath({ screen: 'roleDetail', id }))}
            onOpenProject={(id) => navigate(routePath({ screen: 'projectDetail', id }))}
            onOpenPerson={(personId) =>
              navigate(routePath({ screen: 'peopleDetail', id: personId, from: routePath(route) }))}
            onBackToList={() => navigate(routePath({ screen: 'auditions', stage: 'all' }))}
          />
        );
        strip = undefined;
        break;
      }
      // No Strip here (NavZoneProps' own doc: omitted entirely, not just
      // empty, for a screen with no sub-view to switch) -- this is a
      // single-record drill-in, same as the blessed demo's aud1 screen,
      // which shows no subnav either. "Back" always lands on the
      // unfiltered Auditions list (routePath's own {stage:'all'} default),
      // same convention as Materials' onBack always landing on Today.
      screen = (
        <AuditionDetail
          tenantId={tenantId}
          id={route.id}
          onBack={() => navigate(routePath({ screen: 'auditions', stage: 'all' }))}
          onViewSource={(sourceId) =>
            navigate(routePath({ screen: 'source', id: sourceId, from: routePath(route) }))
          }
        />
      );
      strip = undefined;
      break;
    case 'projects':
      // Same as auditions above: aa_project, not actor_project.
      // A4: the productions, with a DERIVED lens strip — aa_project has no
      // lifecycle, so PROJECT_PHASES below (Auditioned/Booked/Released) has
      // nothing in this pack to filter on.
      if (isActorAssist) {
        screen = (
          <HouseholdProjects
            tenantId={tenantId}
            onOpen={(id) => navigate(routePath({ screen: 'projectDetail', id }))}
          />
        );
        strip = undefined;
        break;
      }
      screen = (
        <Projects
          tenantId={tenantId}
          phase={route.phase}
          onOpen={(id) => navigate(routePath({ screen: 'projectDetail', id }))}
        />
      );
      strip = (
        <Strip
          items={PROJECT_PHASES}
          activeId={route.phase}
          onSelect={(phase) => navigate(routePath({ screen: 'projects', phase }))}
        />
      );
      break;
    case 'roleDetail':
      // A4 — the Role: *everything about this part*, and the owner of the
      // sides revision trail. The one screen reached from two directions —
      // down from a project, up from an audition's breadcrumb.
      screen = (
        <RoleSurface
          tenantId={tenantId}
          id={route.id}
          onOpenProject={(id) => navigate(routePath({ screen: 'projectDetail', id }))}
          onOpenAudition={(id) => navigate(routePath({ screen: 'auditionDetail', id }))}
          onOpenEngagement={(id) => navigate(routePath({ screen: 'engagementDetail', id }))}
          onBackToProjects={() => navigate(routePath({ screen: 'projects', phase: 'all' }))}
          onOpenSubmission={(id) => navigate(routePath({ screen: 'submissionDetail', id }))}
        />
      );
      strip = undefined;
      break;
    // A5 — THE FAR SIDE OF THE SEAM. Both are actor_assist-only surfaces;
    // they are unreachable on a Rexx build because nothing on that rail
    // navigates to them, the same way roleDetail is.
    case 'engagementDetail':
      // The booking. Reached from the ROLE's seam row — which called
      // openRecordEditor until now, i.e. the fallback door used as the front
      // one, the exact defect A4 fixed one record earlier.
      screen = (
        <EngagementSurface
          tenantId={tenantId}
          id={route.id}
          onBack={() => navigate(routePath({ screen: 'projects', phase: 'all' }))}
          onOpenRole={(id) => navigate(routePath({ screen: 'roleDetail', id }))}
          onOpenProject={(id) => navigate(routePath({ screen: 'projectDetail', id }))}
          onOpenAudition={(id) => navigate(routePath({ screen: 'auditionDetail', id }))}
          onOpenDay={(id) => navigate(routePath({ screen: 'shootDay', id }))}
        />
      );
      strip = undefined;
      break;
    case 'shootDay':
      screen = (
        <ShootDayCard
          tenantId={tenantId}
          id={route.id}
          onBack={() => navigate(routePath({ screen: 'today' }))}
          onOpenEngagement={(id) => navigate(routePath({ screen: 'engagementDetail', id }))}
        />
      );
      strip = undefined;
      break;
    case 'tape':
      screen = (
        <TapeRecord
          tenantId={tenantId}
          id={route.id}
          onBack={() => navigate(routePath({ screen: 'materials', section: 'me' }))}
          onOpenRole={(id) => navigate(routePath({ screen: 'roleDetail', id }))}
          onOpenAudition={(id) => navigate(routePath({ screen: 'auditionDetail', id }))}
        />
      );
      strip = undefined;
      break;
    case 'projectDetail':
      if (isActorAssist) {
        screen = (
          <ProjectSpine
            tenantId={tenantId}
            id={route.id}
            onOpenRole={(id) => navigate(routePath({ screen: 'roleDetail', id }))}
            onOpenProject={(id) => navigate(routePath({ screen: 'projectDetail', id }))}
            onBackToProjects={() => navigate(routePath({ screen: 'projects', phase: 'all' }))}
            onViewSource={(sourceId) =>
              navigate(routePath({ screen: 'source', id: sourceId, from: routePath(route) }))
            }
            onOpenPerson={(personId) =>
              navigate(routePath({ screen: 'peopleDetail', id: personId, from: routePath(route) }))
            }
          />
        );
        strip = undefined;
        break;
      }
      // A single-record drill-in — no Strip (same as auditionDetail). Back
      // always lands on the unfiltered Projects list.
      screen = (
        <ProjectDetail
          tenantId={tenantId}
          id={route.id}
          onBack={() => navigate(routePath({ screen: 'projects', phase: 'all' }))}
          onOpenAudition={(id) => navigate(routePath({ screen: 'auditionDetail', id }))}
          onViewSource={(sourceId) =>
            navigate(routePath({ screen: 'source', id: sourceId, from: routePath(route) }))
          }
        />
      );
      strip = undefined;
      break;
    case 'source':
      // The untouched source viewer — back returns to wherever the .srclink
      // was tapped (route.from), mirroring the demo's srcReturn/backFromSrc;
      // a bare source view (no from) falls back to the Projects list.
      screen = (
        <SourceViewer
          tenantId={tenantId}
          id={route.id}
          onBack={() => navigate(route.from ?? routePath({ screen: 'projects', phase: 'all' }))}
        />
      );
      strip = undefined;
      break;
    case 'career':
      screen = (
        <Career
          tenantId={tenantId}
          section={route.section}
          onViewSource={(sourceId) =>
            navigate(routePath({ screen: 'source', id: sourceId, from: routePath(route) }))
          }
          onNavigate={(path) => navigate(path)}
        />
      );
      strip = (
        <Strip
          items={CAREER_SECTIONS}
          activeId={route.section}
          onSelect={(section) => navigate(routePath({ screen: 'career', section }))}
        />
      );
      break;
    case 'materials':
      // A3: a TAB on this pack's rail rather than a workshop reached from
      // Today's strip, and over aa_media/aa_profile rather than the Actor
      // pack's own material types.
      if (isActorAssist) {
        // A6: the Materials tab is the TAPE LOG, not a registry. A gallery
        // answers "what do we have"; a parent needs "what did we do, and did
        // it work" — see screens/HouseholdTapes.tsx.
        //
        // A10: and it is now the tape log, the photos, OR the resume. The
        // resume belongs here rather than on a tab of its own — it is a
        // material, the same way a headshot is, and the rail is already the
        // longest thing on this pack's screen. `photos` joined 2026-09-20 —
        // `aa_media`'s own `kind: 'photo'` had never had a screen at all
        // (MaterialsPhotos.tsx's own header).
        //
        // Anything that is neither the resume nor the photos is the tapes: a
        // bare `/materials` parses to the OTHER pack's first section ('me'),
        // which this pack does not have, and landing a person on nothing
        // because a URL was short is not a behaviour worth preserving.
        screen =
          route.section === 'resume' ? (
            // P4 — the deployment's own name, threaded so the printed
            // sheet can carry a mark below Pro without a screen hardcoding
            // one. Same `productName` the reviewer's masthead reads.
            <PerformerResume tenantId={tenantId} productName={productName} />
          ) : route.section === 'headshots' ? (
            <MaterialsPhotos tenantId={tenantId} kind="headshot" />
          ) : route.section === 'reels' ? (
            <MaterialsReelsAssist tenantId={tenantId} />
          ) : route.section === 'photos' ? (
            <MaterialsPhotos tenantId={tenantId} />
          ) : (
            <HouseholdTapes
              tenantId={tenantId}
              onOpenTape={(id) => navigate(routePath({ screen: 'tape', id }))}
              // F3 — "keep everything" lands on Settings, because Settings is
              // where this app actually names the plan: the tier's identity
              // and the storage meter are both there, read from the same
              // resolution that refuses an upload. There is no plans SCREEN
              // in the app today and Free has no checkout at all (decision
              // `626dcf5a` — TIFF needs no Stripe), so sending a parent to a
              // purchase flow that does not exist would be the dishonest
              // option. FLAGGED for Atlas: when B1's billing surface lands,
              // this is the one line that moves.
              onUpgrade={() => navigate(routePath({ screen: 'settings' }))}
            />
          );
        strip = (
          <Strip
            items={AA_MATERIALS_SECTIONS}
            activeId={
              route.section === 'resume'
                ? 'resume'
                : route.section === 'photos'
                  ? 'photos'
                  : route.section === 'headshots'
                    ? 'headshots'
                    : route.section === 'reels'
                      ? 'reels'
                      : 'tapes'
            }
            onSelect={(section) => navigate(routePath({ screen: 'materials', section }))}
          />
        );
        break;
      }
      screen = (
        <Materials
          tenantId={tenantId}
          userName={userName}
          section={route.section}
          onBack={() => navigate(routePath({ screen: 'today' }))}
          onNavigate={(section) => navigate(routePath({ screen: 'materials', section }))}
        />
      );
      strip = (
        <Strip
          items={MATERIALS_SECTIONS}
          activeId={route.section}
          onSelect={(section) => navigate(routePath({ screen: 'materials', section }))}
        />
      );
      break;
    // A3 — the pack's heads. Each is the shared registry over the types
    // householdTabs.ts names for it; `stats` is the one that is not a list.
    //
    // ⚑ `people` IS NOT ONE OF THEM UNCONDITIONALLY — see the case below.
    // The comment that used to sit here said all four were "gated to an
    // actor_assist build above (ACTOR_ASSIST_SCREENS), so these cases are
    // unreachable elsewhere." That stopped being true at the 2026-08-26 trunk
    // merge, which added `people` to PERSONAL_SCREENS as well.
    case 'documents':
    case 'development': {
      const tab = householdTab(route.screen);
      screen = tab ? (
        <Registry
          tenantId={tenantId}
          eyebrow={tab.eyebrow}
          sections={tab.sections}
          // ⚑ W6 (`2f377423`) — THE CRAFT PANEL SITS ON DEVELOPMENT, which is
          // the pack's own "Craft" nav group (verticals/actor-assist/index.ts:
          // Materials · Development). It is passed IN rather than wrapped
          // around, so the tab keeps ONE scrolling root — see RegistryProps.lead.
          //
          // It is not a new screen and deliberately so: `development` already
          // has a door (a declared rail tab, which ScreenReachability.test.ts
          // records under REACHED_OTHERWISE), and a second screen for the same
          // subject would be the duplicate-surface shape that guard was written
          // after.
          lead={
            route.screen === 'development' ? (
              <CraftPanel tenantId={tenantId} onNavigate={navigate} />
            ) : undefined
          }
        />
      ) : null;
      strip = undefined;
      break;
    }
    // ⚑ `people` IS TWO SCREENS, ONE PER VERTICAL — and until this fix the
    // cockpit's was DEAD CODE.
    //
    // The 2026-08-26 trunk merge left TWO `case 'people':` arms in this one
    // switch: this one, and the cockpit's `<People>` further down. A switch
    // takes the FIRST match, so the second never ran — esbuild said so on
    // every build ("this case clause will never be evaluated because it
    // duplicates an earlier case clause") and nothing read the warning.
    //
    // It was not caught by the gate above either, because the gate is
    // CORRECT: `people` is in BOTH ACTOR_ASSIST_SCREENS and PERSONAL_SCREENS,
    // so `packOwns` is true for either vertical and the route arrives here
    // intact on both. The gate routes; the switch decides; only the switch
    // was wrong.
    //
    // The live consequence, on Huan's own cockpit: HOUSEHOLD_TABS is a static
    // map with no notion of vertical, so `householdTab('people')` handed a
    // personal build the ACTOR pack's people tab — a Registry over `aa_*`
    // record types the personal pack does not declare. The People tab showed
    // the wrong screen over vocabulary that pack has never heard of.
    // ⚑ W2 (`9753e5ed`) — AND THE HOUSEHOLD'S HALF IS NO LONGER A REGISTRY.
    // It was the generic list over `aa_person`: names, and nothing a family
    // did not type in themselves. HouseholdPeople derives the position, the
    // production and the read count from `aa_crew_credit` and the auditions'
    // own `people` links, so the screen answers "who do we know here" rather
    // than reciting a phone book. `householdTabs.ts` no longer declares
    // `people` at all — the A4 precedent, where a table entry nothing reads
    // is a claim about the app that has stopped being true.
    case 'people':
      if (isActorAssist) {
        screen = (
          <HouseholdPeople
            tenantId={tenantId}
            onOpen={(id) => navigate(routePath({ screen: 'peopleDetail', id, from: routePath(route) }))}
          />
        );
      } else {
        // B17c — the people, and one person's spread.
        screen = (
          <People
            tenantId={tenantId}
            onOpen={(id) => navigate(routePath({ screen: 'peopleDetail', id, from: routePath(route) }))}
          />
        );
      }
      strip = undefined;
      break;
    case 'stats':
      screen = <HouseholdStats tenantId={tenantId} />;
      strip = undefined;
      break;
    case 'account':
      // ⚑ MERGE (2026-08-26) — THE ONE HUNK THAT IS A PRODUCT DECISION, NOT A
      // MERGE. Rexx CONSOLIDATED Account into a single Settings screen
      // (deliberate: there was a thinner duplicate nothing navigated to).
      // Lane B had put B17d's WORKSPACE SWITCHER on Account — the only way
      // Huan reaches the Front Door tenant to see registrants.
      //
      // Taking Rexx's consolidation, because it is the newer deliberate
      // choice and Account survives as the home of PasswordForm/
      // WorkspaceSwitcher that Settings imports. The switcher was PORTED into
      // Settings rather than dropped — it is the only route to the Front Door
      // tenant, and a merge that removed a door silently is the failure this
      // codebase keeps refusing.
      screen = (
        <Settings
          tenantId={tenantId}
          user={auth.user}
          packVertical={packVertical}
          onUserUpdated={(u) => setAuth({ phase: 'signed-in', user: u })}
          onBack={() => navigate(routePath({ screen: 'today' }))}
          // B17d, restored after the Rexx merge moved this screen: the same
          // two writes TenantResolver makes at login, then Today — the new
          // workspace's own landing, with its own pack skin.
          onSwitchTenant={(t) => {
            setStoredTenantId(t.tenantId);
            setStoredTenantVertical(t.vertical);
            setTenantId(t.tenantId);
            navigate(routePath({ screen: 'today' }));
          }}
        />
      );
      strip = undefined;
      break;
    case 'settings':
      // C6b (wf05 §03): preferences + connections + the record — cross-
      // cutting like Account, no Strip. The workspace switcher rides here
      // too: the gear is the shell's ONLY door to this surface, and a
      // switcher reachable solely by hand-typing #/account is a removed
      // door wearing a working one's route.
      screen = (
        <Settings
          tenantId={tenantId}
          user={auth.user}
          packVertical={packVertical}
          // S1c — the Google OAuth callback's outcome, carried across from
          // the legacy `/#/personal/accounts/<outcome>` redirect below.
          connectedAccountsOutcome={route.screen === 'settings' ? route.state : undefined}
          onUserUpdated={(u) => setAuth({ phase: 'signed-in', user: u })}
          onBack={() => navigate(routePath({ screen: 'today' }))}
          onSwitchTenant={(t) => {
            setStoredTenantId(t.tenantId);
            setStoredTenantVertical(t.vertical);
            setTenantId(t.tenantId);
            navigate(routePath({ screen: 'today' }));
          }}
        />
      );
      strip = undefined;
      break;
    // ---- S1: the Rexx frame (only reachable on a Rexx build — see the
    // route gate above). The two real screens, then the honest stubs:
    // every rail destination this chunk does not build renders a calm,
    // specific placeholder naming what lands there and that it is not
    // built yet (wf02 §01b: "dispatch surfaces are stubs here… and say so
    // when clicked") — never a hidden tab, never a dead end. ----
    case 'workOrders':
      screen = (
        <WorkOrders
          tenantId={tenantId}
          state={route.state}
          onOpen={(id) => navigate(routePath({ screen: 'workOrderDetail', id }))}
          onFilter={(state) =>
            navigate(routePath(state ? { screen: 'workOrders', state } : { screen: 'workOrders' }))
          }
        />
      );
      strip = undefined;
      break;
    case 'workOrderDetail':
      screen = (
        <WorkOrderSpread
          tenantId={tenantId}
          id={route.id}
          onBack={() => navigate(routePath({ screen: 'workOrders' }))}
          onNavigate={(path) => navigate(path)}
        />
      );
      strip = undefined;
      break;
    // ---- S4: the registry — the hierarchy is the navigation (wf02 §03).
    // Every screen takes the generic onNavigate and builds its own routes
    // with routePath, same convention as RexxHome. ----
    case 'clients':
      screen = <Clients tenantId={tenantId} onNavigate={(path) => navigate(path)} />;
      strip = undefined;
      break;
    case 'clientDetail':
      screen = <ClientDetail tenantId={tenantId} id={route.id} onNavigate={(path) => navigate(path)} />;
      strip = undefined;
      break;
    case 'siteDetail':
      screen = <SiteDetail tenantId={tenantId} id={route.id} onNavigate={(path) => navigate(path)} />;
      strip = undefined;
      break;
    case 'money':
      // A3: the one tab key BOTH packs declare. Rexx's Money is still the
      // placeholder below; a household's is the ledger group's real
      // payments and expenses. This branch is the reason the route gate is
      // a pair of sets rather than one — see ACTOR_ASSIST_SCREENS.
      if (isActorAssist) {
        // A5: the household's money is no longer the thin registry. Two
        // questions in the order a parent has them — what is owed and how
        // late, then whether her share should have been set aside — because
        // rows are the wrong shape for obligations: A ROW CANNOT BE LATE.
        screen = <HouseholdMoney tenantId={tenantId} />;
        strip = undefined;
        break;
      }
      screen = (
        <PlaceholderScreen
          eyebrow="Money"
          line="The chase in one place — invoices, payments and deposits, aged so the next call is obvious."
          soft="Not built yet."
        />
      );
      strip = undefined;
      break;
    case 'techs':
      screen = <Techs tenantId={tenantId} onNavigate={(path) => navigate(path)} />;
      strip = undefined;
      break;
    // S11 — the asks inbox. No Strip: its two lenses are part of the screen
    // (a Strip is a TAB's sub-view switcher, and this screen has no tab).
    case 'asks':
      screen = (
        <Asks
          tenantId={tenantId}
          view={route.view}
          {...(route.id === undefined ? {} : { focusId: route.id })}
          onNavigate={(path) => navigate(path)}
        />
      );
      strip = undefined;
      break;
    case 'techDetail':
      screen = <TechDetail tenantId={tenantId} id={route.id} onNavigate={(path) => navigate(path)} />;
      strip = undefined;
      break;
    case 'board':
      // S3 (wf04): the dispatch board is real. Job selection is EPHEMERAL
      // component state, never a route — the board is one surface, and which
      // card is lit is not an address.
      screen = <Dispatch tenantId={tenantId} userName={userName} />;
      strip = undefined;
      break;
    case 'schedule':
      screen = (
        <PlaceholderScreen
          eyebrow="Schedule"
          line="The week's visits, by tech and by day."
          soft="Not built yet — dispatch stays manual for now."
        />
      );
      strip = undefined;
      break;
    case 'map':
      screen = (
        // S3: the board already answers "where are they" from the last job
        // each tech logged — so this stays a placeholder rather than a second
        // door onto the same surface, and it now names what a real map is
        // still waiting on (coordinates on a site record, and a tile
        // provider) instead of claiming nothing exists.
        <PlaceholderScreen
          eyebrow="Map"
          line="Last known positions sit on the board. A drawn map waits on coordinates a site record does not carry yet."
          soft="Not built yet — and it will not be drawn until the geography is real."
        />
      );
      strip = undefined;
      break;
    case 'numbers':
      screen = (
        <PlaceholderScreen
          eyebrow="Numbers"
          line="The owner's view — invoiced, received, deposited and net, over time."
          soft="Not built yet."
        />
      );
      strip = undefined;
      break;
    // S5a (wf03) — THE JOB. The bare tab means "the job I am on now" and
    // the screen resolves which that is from the day; `/jobs/detail/:id`
    // names a work order outright. One screen, one read, both doors.
    //
    // CV3 §1 drop 1 — WHEN THE BARE TAB RESOLVES ITS JOB, THE ROUTE SAYS SO.
    // `{screen:'jobs'}` carries no id, so it is not in
    // lib/captureSubject.ts and every capture taken from the Jobs TAB —
    // the tech's ordinary door to the exact screen built for capturing —
    // fell to the generic inbox while the identical screen at
    // `/jobs/detail/:id` attached (WO-4006's leak). The screen resolves
    // WHICH work order it is showing (lib/fieldDay's currentJob); once it
    // has, the route is NORMALIZED to the canonical `/jobs/detail/:id` (a
    // REPLACE, so the back button never loops through the bare tab), and
    // the existing subject table then just works — for the typed sheet, the
    // hold's queue envelope and the intent lane alike. Chosen over having
    // the screen surface its subject to the shell because one mechanism
    // (the route carries the subject) beats two, the URL stops lying about
    // what is on screen, and the tab model is unharmed: jobDetail lights
    // the same Jobs tab, and pressing the tab again re-enters bare `/jobs`,
    // which re-resolves "the job I am on NOW" fresh.
    case 'jobs':
      screen = (
        <Job
          tenantId={tenantId}
          onNavigate={(path) => navigate(path)}
          onCapture={pressKnot}
          onResolvedWorkOrder={(woId) =>
            navigate(routePath({ screen: 'jobDetail', id: woId }), { replace: true })
          }
        />
      );
      strip = undefined;
      break;
    case 'jobDetail':
      screen = (
        <Job
          tenantId={tenantId}
          id={route.id}
          onNavigate={(path) => navigate(path)}
          onCapture={pressKnot}
        />
      );
      strip = undefined;
      break;
    // S5b (wf03 §02, decision 817a0489) — ONE HEAD, TWO PEOPLE. The same
    // rule the default case below already applies to Today: role decides
    // which surface `chat` means. The OFFICE reads its conversations here.
    //
    // CV2 (decision 8a36f2de): the TECH side is now the real in-app chat.
    // S5b's door-to-Telegram screen existed because core's `laneVisibleTo`
    // granted a tech no lane at all; CV1 changed that fact at the engine
    // (derived ∪ granted job lanes, the member's own two DM lanes), so the
    // screen the fact justified retired with it. Telegram stays reachable
    // as a quiet line inside the DM thread — demoted, not removed.
    case 'chat':
      screen = techFrame ? (
        <TechChat tenantId={tenantId} onNavigate={(path) => navigate(path)} />
      ) : (
        <Conversations tenantId={tenantId} onNavigate={(path) => navigate(path)} />
      );
      strip = undefined;
      break;
    // One conversation, addressed (`/chat/<lane>/<id>`) — both frames now
    // render a real thread; the SERVER still decides what each caller may
    // read (the §1 gate 403s an unreachable record, and that refusal
    // renders in its own words rather than as an empty conversation).
    case 'conversation':
      screen = techFrame ? (
        <TechChat
          tenantId={tenantId}
          open={{ lane: route.lane, id: route.id }}
          onNavigate={(path) => navigate(path)}
        />
      ) : (
        <Conversations
          tenantId={tenantId}
          open={{ lane: route.lane, id: route.id }}
          onNavigate={(path) => navigate(path)}
        />
      );
      strip = undefined;
      break;
    // S5a §6, as amended by Huan 2026-08-14 — THE PAY TAB STAYS, AND SAYS
    // THE TRUE REASON IT IS EMPTY.
    //
    // The authority model forbids the screen today: `readScope` covers five
    // types and `hvac_pay_profile` is not one of them (S8 moved pay off the
    // tech profile to an office-only record, and decision 1e1d2874 was
    // partly reversed so a tech no longer sees their own rate even in
    // Settings). So the tab would render a 403 or a blank. Whether to
    // row-scope a tech to their own pay profile, derive an earnings view
    // from their own visits, or drop the tab is a PRODUCT question for
    // Huan, not something to resolve in code — and nothing here adds the
    // type to readScope or touches the manifest.
    //
    // What the page does instead is state the reason ("your pay lives with
    // the office for now") rather than apologise, and name the intent
    // without inventing a date. Roles, never a person.
    case 'pay':
      screen = (
        <PlaceholderScreen
          eyebrow="Pay"
          line="Your pay lives with the office for now."
          soft="This screen arrives in a later version of Rexx."
        />
      );
      strip = undefined;
      break;
    // B16a — the cockpit's screens. The state filter rides the Strip, the
    // same sub-view mechanism every other filtered list here uses.
    case 'threads':
      screen = (
        <Threads
          tenantId={tenantId}
          state={route.state}
          onOpen={(id) => navigate(routePath({ screen: 'threadDetail', id, from: routePath(route) }))}
        />
      );
      strip = (
        <Strip
          items={threadStateOptions()}
          activeId={route.state ?? 'all'}
          onSelect={(state) =>
            navigate(routePath(state === 'all' ? { screen: 'threads' } : { screen: 'threads', state }))
          }
        />
      );
      break;
    case 'threadDetail': {
      // Back and Done both land where you CAME from (route.from), named.
      const threadParent = parentRoute(route) ?? { screen: 'threads', state: 'waiting_me' };
      screen = (
        <ThreadSpread
          tenantId={tenantId}
          id={route.id}
          backLabel={routeLabel(threadParent)}
          onBack={() => navigate(routePath(threadParent))}
        />
      );
      strip = undefined;
      break;
    }
    case 'review':
      screen = <Review tenantId={tenantId} navigate={navigate} />;
      strip = undefined;
      break;
    // Build 1 of the 01B nav — the Personal tab: Calendar · To-dos · Lists ·
    // People · Notes, the sub-tab in the hash. Its own strip of sub-tabs sits
    // at the top of the screen (plate 01B); the shell's strip slot carries
    // only the To-dos filter, exactly where the To-dos tab always had it.
    case 'personal': {
      const from = routePath(route);
      screen = (
        <Personal
          tenantId={tenantId}
          sub={route.sub}
          state={route.state}
          listId={route.listId}
          navigate={navigate}
          onOpenEvent={(id) => navigate(routePath({ screen: 'calendarDetail', id, from }))}
          onOpenThread={(id) => navigate(routePath({ screen: 'threadDetail', id, from }))}
          onOpenPerson={(id) => navigate(routePath({ screen: 'peopleDetail', id, from }))}
        />
      );
      strip =
        route.sub === 'todos' ? (
          <Strip
            items={todoStateOptions()}
            activeId={route.state ?? 'now'}
            onSelect={(state) =>
              navigate(
                routePath(
                  state === 'now' ? { screen: 'personal', sub: 'todos' } : { screen: 'personal', sub: 'todos', state }
                )
              )
            }
          />
        ) : undefined;
      break;
    }
    // The Audience tab (decision `4804fc96`). Its own sub-tab strip
    // (Home · Inbox · Week) lives INSIDE the screen, same shape as
    // Personal just above — the shell's strip slot carries nothing extra.
    case 'audience':
      screen = (
        <Audience
          tenantId={tenantId}
          sub={route.sub}
          weekStart={route.weekStart}
          navigate={navigate}
          onOpenReview={() => navigate(routePath({ screen: 'review' }))}
          onOpenInboxItem={(id) => navigate(routePath({ screen: 'audienceInboxDetail', id }))}
        />
      );
      strip = undefined;
      break;
    case 'audienceInboxDetail': {
      const inboxParent = parentRoute(route) ?? { screen: 'audience', sub: 'inbox' };
      screen = (
        <AudienceInboxDetail
          tenantId={tenantId}
          id={route.id}
          backLabel={routeLabel(inboxParent)}
          onBack={() => navigate(routePath(inboxParent))}
          onOpenThread={(threadId) => navigate(routePath({ screen: 'threadDetail', id: threadId }))}
        />
      );
      strip = undefined;
      break;
    }
    // Chunk PRG-W — the Delivery list and one programme's own screen.
    case 'delivery':
      screen = <Delivery tenantId={tenantId} onOpenProgramme={(pid) => navigate(routePath({ screen: 'programmeDetail', id: pid }))} />;
      strip = undefined;
      break;
    case 'programmeDetail': {
      const deliveryParent = parentRoute(route) ?? { screen: 'delivery' };
      screen = (
        <Programme
          tenantId={tenantId}
          id={route.id}
          onBack={() => navigate(routePath(deliveryParent))}
          onOpenReview={() => navigate(routePath({ screen: 'review' }))}
        />
      );
      strip = undefined;
      break;
    }
    // Chunk SYS-W — who is working on what within what limits.
    case 'systems':
      screen = <Systems tenantId={tenantId} />;
      strip = undefined;
      break;
    // B17a — the agenda and one event's preparation.
    case 'calendar':
      // A3/A4 pattern: same route, different pack, different screen. The
      // household's agenda is built from aa_audition/aa_session/aa_shoot_day
      // plus the holds the pack's own reactions write; the cockpit's is built
      // from calendar events. Neither renders the other's vocabulary, so the
      // branch is here rather than inside a screen that would have to ask
      // which pack it is running under.
      //
      // S1b (decision c7ee4a1b): the PERSONAL pack's own `calendar` surface
      // (cockpitSurfaces.ts) gets its own cockpit screen, plate 03 —
      // Day/Week/Month, the Mist strip, drag-to-move. Every other pack's
      // `#/calendar` (below) is untouched — this branch is checked FIRST
      // and is the only new line here.
      if (isPersonal) {
        // MAIL-POLISH BRIEF addition — the cockpit "+"'s relay for this
        // screen (see `cockpitCreate`/`newEventRequestToken` above).
        screen = <CockpitCalendar tenantId={tenantId} newEventRequestToken={newEventRequestToken} />;
        strip = undefined;
        break;
      }
      if (isActorAssist) {
        screen = (
          <HouseholdCalendar
            tenantId={tenantId}
            onOpenAudition={(id) => navigate(routePath({ screen: 'auditionDetail', id }))}
            onOpenShootDay={(id) => navigate(routePath({ screen: 'shootDay', id }))}
          />
        );
        strip = undefined;
        break;
      }
      screen = (
        <Calendar
          tenantId={tenantId}
          onOpen={(id) => navigate(routePath({ screen: 'calendarDetail', id, from: routePath(route) }))}
        />
      );
      strip = undefined;
      break;
    // 2026-09-02 — the household's mail: what arrived, and what it became.
    // S1 (2026-10-15) made this a SHARED head, same shape as `calendar`
    // above: the cockpit's own Gmail-backed Mail screen now answers it on
    // the personal pack, over a completely different store (the GET
    // /api/mail/ routes plus the thread spine's own gmail context) —
    // neither rendering shares a line with the other.
    case 'mail':
      // ⚠ ONLY ONE LOOP-EXIT STATEMENT FOR THIS WHOLE CASE, ON PURPOSE (not
      // the early-return shape `calendar` above uses). ScreenReachability
      // .test.ts's own "Mail has its own, independent door to the review
      // queue" check slices this case from its `case 'mail':` line to its
      // first such statement and greps that slice for the onOpenReview
      // wiring below — an early exit inside an `if (isPersonal)` branch
      // would slice the test's read off before it ever reaches
      // HouseholdMail's JSX, failing a passing door as if it had gone
      // missing. if/else keeps both branches' source inside the one slice.
      if (isPersonal) {
        screen = (
          <Mail
            tenantId={tenantId}
            // S1c — the reconnect link's new home: Settings › Connected
            // accounts (moved from Personal › Accounts).
            onOpenConnectedAccounts={() => navigate(routePath({ screen: 'settings' }))}
            // MAIL-POLISH BRIEF addition — the cockpit "+"'s relay (see
            // `cockpitCreate`/`composeRequestToken` above): a bumped token,
            // never a boolean, so a second tap while Compose is already
            // open from the first still registers once the sheet closes.
            composeRequestToken={composeRequestToken}
          />
        );
      } else {
        screen = (
          <HouseholdMail
            tenantId={tenantId}
            onOpenToday={() => navigate(routePath({ screen: 'today' }))}
            onOpenReview={() => navigate(routePath({ screen: 'review' }))}
            onOpenAudition={(id) => navigate(routePath({ screen: 'auditionDetail', id }))}
            onOpenShootDay={(id) => navigate(routePath({ screen: 'shootDay', id }))}
            onOpenProject={(id) => navigate(routePath({ screen: 'projectDetail', id }))}
            onOpenRole={(id) => navigate(routePath({ screen: 'roleDetail', id }))}
            onOpenEngagement={(id) => navigate(routePath({ screen: 'engagementDetail', id }))}
            onOpenSource={(id) =>
              navigate(routePath({ screen: 'source', id, from: routePath(route) }))
            }
          />
        );
      }
      strip = undefined;
      break;
    // B17 §2 — what the Knot kept. Same posture as `mail` above: SCREEN_OWNERS
    // already restricts this route to actor_assist, so no vertical branch is
    // needed here either.
    case 'notes':
      screen = <HouseholdNotes tenantId={tenantId} />;
      strip = undefined;
      break;
    // B20a — the front door before the audition. SCREEN_OWNERS already
    // restricts it to actor_assist, so no vertical branch is needed here
    // either — the shape `mail`/`notes` above take.
    //
    // ⚑ B21 (2026-09-27) — THE LIST IS GONE FROM HERE. It lives on the
    // Auditions screen now (the `auditions` case above); only the spread
    // keeps its own case.
    case 'submissionDetail':
      screen = (
        <SubmissionSpread
          tenantId={tenantId}
          id={route.id}
          onOpenAudition={(id) => navigate(routePath({ screen: 'auditionDetail', id }))}
          onBackToList={() => navigate(routePath({ screen: 'auditions', stage: 'submissions' }))}
          onOpenSource={(sourceId) => navigate(routePath({ screen: 'source', id: sourceId, from: routePath(route) }))}
        />
      );
      strip = undefined;
      break;
    // B8 — the performer's public page. Cross-cutting like account/settings
    // (no rail slot; SCREEN_OWNERS restricts it to actor_assist already, so
    // no vertical branch is needed here either — the shape `mail` above
    // takes). Reached from More → Your page; `mode="more"` labels the
    // onward button "Done" and offers the unpublish control once live.
    case 'yourPage':
      screen = <YourPage tenantId={tenantId} mode="more" onDone={() => navigate(routePath({ screen: 'today' }))} />;
      strip = undefined;
      break;
    // B17b — the to-dos, filter in the hash via the Strip.
    case 'todos':
      screen = (
        <Todos
          tenantId={tenantId}
          state={route.state}
          onOpenThread={(id) => navigate(routePath({ screen: 'threadDetail', id, from: routePath(route) }))}
        />
      );
      strip = (
        <Strip
          items={todoStateOptions()}
          activeId={route.state ?? 'now'}
          onSelect={(state) =>
            navigate(routePath(state === 'now' ? { screen: 'todos' } : { screen: 'todos', state }))
          }
        />
      );
      break;
    case 'calendarDetail': {
      const calParent = parentRoute(route) ?? { screen: 'calendar' };
      screen = (
        <CalendarSpread
          tenantId={tenantId}
          id={route.id}
          backLabel={routeLabel(calParent)}
          onBack={() => navigate(routePath(calParent))}
          onOpenThread={(id) => navigate(routePath({ screen: 'threadDetail', id, from: routePath(route) }))}
        />
      );
      strip = undefined;
      break;
    }
    // B18d — what every client is telling us, by bucket.
    case 'buckets':
      screen = (
        <ClientBuckets
          tenantId={tenantId}
          onOpenThread={(id) => navigate(routePath({ screen: 'threadDetail', id, from: routePath(route) }))}
          onOpenAccount={(id) => navigate(routePath({ screen: 'accountSpread', id }))}
        />
      );
      strip = undefined;
      break;
    // K1 (console kernel, c2-account-kernel.md) — the Account, whole.
    case 'accountSpread':
      screen = (
        <AccountSpread
          tenantId={tenantId}
          id={route.id}
          onBack={() => navigate(routePath({ screen: 'buckets' }))}
          onOpenEngagement={(id) => navigate(routePath({ screen: 'engagementSpread', id }))}
          onNewEngagement={() =>
            openRecordEditor({ mode: 'create', typeKey: 'engagement', prefillFields: { account_id: route.id } })
          }
        />
      );
      strip = undefined;
      break;
    // K2's consumer (c3-lifecycle-kernel.md) — one bounded engagement.
    case 'engagementSpread':
      screen = (
        <EngagementSpread
          tenantId={tenantId}
          id={route.id}
          onBack={() => navigate(routePath({ screen: 'buckets' }))}
          onOpenAccount={(accountId) => navigate(routePath({ screen: 'accountSpread', id: accountId }))}
        />
      );
      strip = undefined;
      break;
    // R3b — the AI Radar.
    case 'radar':
      screen = (
        <RadarHome
          tenantId={tenantId}
          onOpenTopic={(slug) => navigate(routePath({ screen: 'radarTopic', slug }))}
          onOpenTopics={() => navigate(routePath({ screen: 'radarTopics' }))}
          onOpenProposal={(id) => navigate(routePath({ screen: 'radarProposal', id }))}
          onOpenNewsletter={() => navigate(routePath({ screen: 'radarNewsletter' }))}
        />
      );
      strip = undefined;
      break;
    case 'radarTopic':
      screen = (
        <RadarTopic
          tenantId={tenantId}
          slug={route.slug}
          runId={route.runId}
          onBack={() => navigate(routePath({ screen: 'radar' }))}
          onOpenProposal={(id) => navigate(routePath({ screen: 'radarProposal', id }))}
          onOpenRun={(runId) => navigate(routePath({ screen: 'radarTopic', slug: route.slug, runId }))}
          onOpenPage={(slug) => navigate(routePath({ screen: 'recordPage', slug }))}
        />
      );
      strip = undefined;
      break;
    case 'radarProposal':
      screen = <RadarProposal tenantId={tenantId} id={route.id} onBack={() => navigate(routePath({ screen: 'radar' }))} />;
      strip = undefined;
      break;
    case 'radarTopics':
      screen = (
        <RadarTopics
          tenantId={tenantId}
          onBack={() => navigate(routePath({ screen: 'radar' }))}
          onOpenTopic={(slug) => navigate(routePath({ screen: 'radarTopic', slug }))}
          onOpenNewsletter={() => navigate(routePath({ screen: 'radarNewsletter' }))}
        />
      );
      strip = undefined;
      break;
    case 'radarNewsletter':
      screen = <RadarNewsletter tenantId={tenantId} onBack={() => navigate(routePath({ screen: 'radar' }))} />;
      strip = undefined;
      break;
    // C1 (2026-09-28) — the Record window.
    case 'record':
      screen = (
        <RecordHome
          tenantId={tenantId}
          onOpenPage={(slug) => navigate(routePath({ screen: 'recordPage', slug }))}
          onOpenFile={(id) => navigate(routePath({ screen: 'recordFile', id }))}
          onOpenBrowse={(axis) => navigate(routePath({ screen: 'recordBrowse', axis }))}
          onOpenReading={() => navigate(routePath({ screen: 'recordReading' }))}
        />
      );
      strip = undefined;
      break;
    case 'recordPage':
      screen = (
        <RecordPage
          tenantId={tenantId}
          slug={route.slug}
          onBack={() => navigate(routePath({ screen: 'record' }))}
          onOpenPage={(slug) => navigate(routePath({ screen: 'recordPage', slug }))}
        />
      );
      strip = undefined;
      break;
    case 'recordFile':
      screen = <RecordFile tenantId={tenantId} id={route.id} onBack={() => navigate(routePath({ screen: 'record' }))} />;
      strip = undefined;
      break;
    case 'recordBrowse':
      screen = (
        <RecordBrowse
          tenantId={tenantId}
          axis={route.axis}
          value={route.value}
          onBack={() => navigate(routePath({ screen: 'record' }))}
          onOpenBrowse={(axis, value) => navigate(routePath({ screen: 'recordBrowse', axis, value }))}
          onOpenItem={(item) => {
            if (item.kind === 'file') navigate(routePath({ screen: 'recordFile', id: item.id }));
            else if (item.slug) navigate(routePath({ screen: 'recordPage', slug: item.slug }));
          }}
        />
      );
      strip = undefined;
      break;
    case 'recordReading':
      screen = (
        <RecordReading
          tenantId={tenantId}
          onBack={() => navigate(routePath({ screen: 'record' }))}
          onOpenItem={(item) => {
            if (item.kind === 'file') navigate(routePath({ screen: 'recordFile', id: item.id }));
            else if (item.slug) navigate(routePath({ screen: 'recordPage', slug: item.slug }));
          }}
        />
      );
      strip = undefined;
      break;
    // B17c's `case 'people':` used to sit HERE, and never ran — it duplicated
    // the arm far above and a switch takes the first match. Its body now
    // lives there, in the vertical branch, so the cockpit actually gets it.
    case 'peopleDetail': {
      const personParent = parentRoute(route) ?? { screen: 'people' };
      // W2: one head, two verticals, exactly as `people` above. A household's
      // person is their credits and the reads that named them; the cockpit's
      // is B17c's contact spread, and neither screen has to know the other
      // exists.
      if (isActorAssist) {
        screen = (
          <HouseholdPerson
            tenantId={tenantId}
            id={route.id}
            backLabel={routeLabel(personParent)}
            onBack={() => navigate(routePath(personParent))}
            onOpenAudition={(auditionId) => navigate(routePath({ screen: 'auditionDetail', id: auditionId }))}
            onOpenProject={(projectId) => navigate(routePath({ screen: 'projectDetail', id: projectId }))}
          />
        );
        strip = undefined;
        break;
      }
      screen = (
        <PersonSpread
          tenantId={tenantId}
          id={route.id}
          backLabel={routeLabel(personParent)}
          onBack={() => navigate(routePath(personParent))}
          onOpenThread={(threadId) => navigate(routePath({ screen: 'threadDetail', id: threadId, from: routePath(route) }))}
        />
      );
      strip = undefined;
      break;
    }
    // K3 — the Agreement. URL-only for now (ScreenReachability's own
    // REACHED_OTHERWISE entry says why); the Account spread (K1, a sibling
    // chunk) links here once it lands.
    case 'agreements':
      screen = (
        <AgreementsList
          tenantId={tenantId}
          onOpen={(agreementId) => navigate(routePath({ screen: 'agreementSpread', id: agreementId }))}
        />
      );
      strip = undefined;
      break;
    case 'agreementSpread': {
      const agreementParent = parentRoute(route) ?? { screen: 'agreements' };
      screen = (
        <AgreementSpread
          tenantId={tenantId}
          id={route.id}
          backLabel={routeLabel(agreementParent)}
          onBack={() => navigate(routePath(agreementParent))}
        />
      );
      strip = undefined;
      break;
    }
    default:
      // S1: on a Rexx build, Today IS the admin home (wf02 §02 — a work
      // surface, not a dashboard; no Materials strip, that's Actor's).
      // Otherwise: Today shows the Materials strip too (no chip active —
      // none of "This is me/Headshots/Resume/Reels" represents Today
      // itself); tapping any of them enters the Materials workshop.
      // A3: on a household build, Today IS the household home (A2 spec
      // §4.1 — "the parent's job is triage across children, and the shell's
      // first screen must answer that question without a click"). Scoped to
      // one performer it is the SAME screen, narrowed — never a second one.
      if (isActorAssist) {
        screen = (
          <HouseholdHome
            tenantId={tenantId}
            userName={userName}
            onNavigate={(to) => navigate(to)}
          />
        );
        strip = undefined;
        break;
      }
      if (isRexx) {
        // S5a: which Today a Rexx member lands on is a ROLE question. Wendy
        // gets the office's work surface; a tech gets their day, in order,
        // thumb-shaped (wf03 §01). Same route, same tab, different job.
        screen = techFrame ? (
          <TechToday tenantId={tenantId} userName={userName} onNavigate={(path) => navigate(path)} />
        ) : (
          <RexxHome tenantId={tenantId} userName={userName} onNavigate={(path) => navigate(path)} />
        );
        strip = undefined;
        break;
      }
      if (isPersonal) {
        // B16a/b: the cockpit's Today — the four receipt groups from
        // GET /api/today, that door's first consumer. One screen, one job:
        // the proposal feed Actor's Today carries lives in Review here, and
        // the Materials strip is Actor's, not this vertical's.
        screen = (
          <CockpitToday
            tenantId={tenantId}
            userName={userName}
            onOpenThread={(id) => navigate(routePath({ screen: 'threadDetail', id, from: '/' }))}
            onNavigate={(path) => navigate(path)}
          />
        );
        strip = undefined;
        break;
      }
      screen = (
        <Today
          tenantId={tenantId}
          userName={userName}
          onAddProject={() => navigate(routePath({ screen: 'projects', phase: 'all' }))}
          onForwardBreakdown={pressKnot}
          onNavigate={(path) => navigate(path)}
        />
      );
      strip = (
        <Strip
          items={MATERIALS_SECTIONS}
          onSelect={(section) => navigate(routePath({ screen: 'materials', section }))}
        />
      );
  }

  // S1c — which switcher surface (cockpitSurfaces.ts) the current screen
  // belongs to, for highlighting the open menu's active row. `buckets` maps
  // to `clients` (the switcher's own key for that screen — see
  // cockpitSurfaces.ts's header on why it isn't `clients` the TopTab).
  // Every screen with no direct switcher door (a thread, a calendar event,
  // a radar topic, …) answers undefined: its own door still works, nothing
  // in the menu lights up, and the title still names the real screen via
  // routeLabel below.
  // NOT a `switch` on `route.screen`: NoShadowedRouteCases.test.ts reads
  // EVERY `case '<screen>':` label in this whole file as one namespace (by
  // design — it cannot tell which switch a label belongs to, and a route-
  // rendering label written twice is exactly the defect it exists to catch;
  // see its own header). A second switch here, reusing the same screen
  // names as labels, misfired that guard — a Set lookup says the same thing
  // without the collision.
  const COCKPIT_DIRECT_SURFACE_SCREENS = new Set<Route['screen']>([
    'today',
    'mail',
    'calendar',
    'personal',
    'review',
    'audience',
    'delivery',
    'systems',
    'radar',
    'record',
    'settings',
  ]);
  const cockpitSurfaceKeyFor = (screen: Route['screen']): string | undefined => {
    if (screen === 'buckets') return 'clients';
    return COCKPIT_DIRECT_SURFACE_SCREENS.has(screen) ? screen : undefined;
  };
  // S1c — the switcher's own counts: the SAME numbers the rail badges show
  // today (review's pending-proposal count, audience's unanswered count —
  // see cockpitSurfaces.ts's COCKPIT_SURFACE_BADGE_TAB for why nothing else
  // is in this object). `undefined` outside the personal pack: the
  // switcher itself never mounts there, but the computation is guarded
  // anyway rather than relying on that.
  const cockpitSurfaceCounts: Partial<Record<string, number>> | undefined = isPersonal
    ? { review: reviewPending, audience: audienceUnanswered }
    : undefined;
  const cockpitTitleSwitcher = isPersonal ? (
    <CockpitTitleSwitcher
      pack={packVertical}
      title={routeLabel(route)}
      activeKey={cockpitSurfaceKeyFor(route.screen)}
      counts={cockpitSurfaceCounts}
      onNavigate={(p) => navigate(p)}
    />
  ) : null;
  // H2 (COCKPIT-HOME-COLUMNS-SPEC.md §H2) — "a Vowric logo mark at the
  // start of the top bar (beside the title switcher) → #/home". Same
  // `navigate(routePath({screen:'home'}))` call every other door into Home
  // uses (the switcher's own menu row, ⌘0) — one path to Home, not a
  // second hand-typed '/home' string. isPersonal-gated same as the
  // switcher itself: every other pack's titlebar is untouched.
  const cockpitHomeMark = isPersonal ? (
    <CockpitHomeMark onNavigate={() => navigate(routePath({ screen: 'home' }))} />
  ) : null;
  // ⚑ TOPBAR BRIEF (2026-10-15) — the two cockpit screens built to FILL the
  // width rather than show one centred reading column (Mail's three panes,
  // Calendar's agenda). Everything else in the personal pack keeps the
  // shared `.app-shell--rail .app-content > *` cap (Today, Settings, …) —
  // this is narrower than `isPersonal` on purpose, named screens only.
  // H1a — the board joins them: a centred reading-column cap left the 4th
  // column with no room to peek at 1440 (found in the walk's own
  // screenshot — a ~160px dead gap between the 3rd column and the pane).
  // H3b — a project screen's own framed site needs the same width (the
  // brief's own "no fixed ~720×420 box"): the 760px reading-column cap
  // would otherwise leave Studio Stays'/VoiceStrip's frame squeezed to a
  // fraction of the content area, same shape as the Mail-screen bug the
  // comment above fixed.
  const cockpitFillScreen =
    isPersonal && (route.screen === 'mail' || route.screen === 'calendar' || route.screen === 'home' || route.screen === 'projectScreen');

  const navProps = {
    active: activeTab,
    onNavigate: (tab: typeof activeTab) => navigate(routePath(defaultRouteFor(tab))),
    knotState: shownKnotState,
    // The hook's own onClick guards the tap path: after a completed HOLD the
    // browser still fires a click, and it must not also reopen the sheet.
    onKnotPress: ptt.onClick,
    destinations,
    knotAmp: ptt.amp,
    onKnotPointerDown: ptt.onPointerDown,
    onKnotPointerUp: ptt.onPointerUp,
    onKnotPointerCancel: ptt.onPointerCancel,
    onKnotLostPointerCapture: ptt.onLostPointerCapture,
    // CV6b §4 — the Chat tab carries the total, in both renderings, off the
    // one store (lib/unread). A pack with no chat tab simply never matches
    // the key, so nothing is drawn and nothing had to be gated.
    badges: {
      chat: { count: unread.count, capped: unread.capped },
      ...(phoneCockpit
        ? {
            review: { count: reviewPending, capped: false },
            audience: { count: audienceUnanswered, capped: false },
          }
        : {}),
    },
    overflow,
    onMorePress: () => setMoreOpen((v) => !v),
    moreOpen,
  };
  const captureSheet = (
    <>
      <CaptureSheet
        open={sheetOpen}
        name={userName}
        tenantId={tenantId}
        context={captureContext}
        onKnotState={(state) => {
          // §2 — any successful action clears the hold's failure toast.
          // `proposing` means the server took the capture (an online text
          // hand-over lands here; a tech never reaches `confirmed`, approve
          // is not theirs); `confirmed` covers approve and queue-on-device.
          // The toast's other two deaths live in the hook itself.
          if (state === 'proposing' || state === 'confirmed') ptt.dismissError();
          setKnotState(state);
        }}
        onClose={closeSheet}
        initialText={spokenText}
        initialQueued={queuedCapture}
        initialIntent={spokenIntent}
        onOpenReview={() => {
          closeSheet();
          navigate(routePath({ screen: 'review' }));
        }}
      />
      {/* A voice capture that fails must SAY so. The push-to-talk hook has
          always set an error string here (mic denied, transcription empty,
          network gone) and nothing ever rendered it, so a failed hold looked
          identical to a hold that did nothing at all. Dismissible, and only
          mounted when there's something to say. */}
      {ptt.error ? (
        <div class="knot-error" role="status" onClick={ptt.dismissError}>
          {ptt.error}
        </div>
      ) : null}
      {/* R-8 (2026-08-30) — the hold lane's "never assume" beat, finally
          MOUNTED. The hook has set pendingConfirm since Core hardening and
          no surface ever rendered it, so a spoken create with an ambiguous
          parent silently dead-ended: state built, dialog never wired — the
          register's named pattern. Same dialog the typed sheet now uses. */}
      {ptt.pendingConfirm ? (
        <KnotConfirmDialog
          tenantId={tenantId}
          targetLabel={ptt.pendingConfirm.targetLabel}
          itemName={ptt.pendingConfirm.itemName}
          parentType={ptt.pendingConfirm.parentType}
          parentLabel={ptt.pendingConfirm.parentLabel}
          candidates={ptt.pendingConfirm.candidates}
          suggestion={ptt.pendingConfirm.suggestion}
          onConfirm={ptt.resolveConfirm}
          onCreateNewParent={ptt.cancelConfirm}
          onCancel={ptt.cancelConfirm}
        />
      ) : null}
      {/* And the rail's own failure, in the same place and for the same
          reason: an empty bar with nothing said is indistinguishable from a
          pack that declares no tabs. */}
      {nav.status === 'failed' && nav.failure ? (
        <NavUnresolved failure={nav.failure} onRetry={nav.retry} />
      ) : null}
    </>
  );
  // R3 — the manual-CRUD engine's two shell-level pieces: the always-present
  // "+" that opens the global create menu, and the overlay host that renders
  // the create/edit/act forms any screen asks for (see lib/recordEditor.ts).
  // S6: the account entry is no longer part of this floating chrome on the
  // rail layout — there it lives INSIDE the rail, at its foot (it used to
  // float over the rail's head). The phone keeps the floating corner
  // placement, rendered in the stack branch below.
  // THE ACCOUNT ENTRY OPENS SETTINGS (Huan, 2026-08-25, from his phone: "I
  // don't see the ability to add admin from preferences screen").
  //
  // There were TWO preferences screens and only one of them had a door.
  // `Settings` holds Connections, Set up again, the office invite, tickets,
  // time off, per-device sign-out and the password form — and NOTHING in the
  // app navigated to it, so all of it was reachable only by typing the URL.
  // The entry every person actually taps went to `Account`: identity,
  // password, sign out. Three separate things Huan asked for this week were
  // built onto a screen with no way in, and he found it on his phone rather
  // than any suite finding it — dead code compiles and greps exactly like
  // live code.
  //
  // Settings is a strict superset (it shows the same email and a better
  // sign-out), so the fix is one screen, not a second link between two.
  const onAccountNavigate = () => navigate(routePath({ screen: 'settings' }));

  // MAIL-POLISH BRIEF addition (Huan, final, 2026-10-16) — the ONE place
  // that decides what the cockpit's "+" does: Mail opens Compose, Calendar
  // opens New event, every other cockpit screen falls back to today's
  // global create menu (byte-identical to the non-personal packs' own "+").
  // Only consulted inside the `isPersonal` cockpit titlebar below — every
  // other pack's GlobalCreate call (`topRightEntries`) never reads this.
  const cockpitCreate: { onClick: () => void; ariaLabel: string } =
    route.screen === 'mail'
      ? { onClick: () => setComposeRequestToken((n) => n + 1), ariaLabel: 'New email' }
      : route.screen === 'calendar'
        ? { onClick: () => setNewEventRequestToken((n) => n + 1), ariaLabel: 'New event' }
        : { onClick: openCreateMenu, ariaLabel: 'Create a record' };

  // CV4 §3 — the shell's top-right CONTROLS, split out from the overlay
  // hosts they used to travel with. The buttons are placed by whichever
  // frame renders them (the phone's `.app-topbar`, the rail's floating
  // corner); the hosts are portals with no placement at all, so bundling
  // them together forced the buttons to stay absolutely positioned.
  const topRightEntries = (
    <>
      {isRexx ? <FindEntry onOpen={() => setFindOpen(true)} /> : null}
      <GlobalCreate />
    </>
  );

  const recordsChrome = <RecordEditorHost tenantId={tenantId} />;

  // ⚑ A3's SCOPE BAR IS DELETED (2026-09-19 ruling). It was the visible,
  // one-tap way back to the household from inside one performer's career;
  // an account is one career now, so there is no scope to leave and nothing
  // for this bar to say.

  // CV5 §2 — THE FRAME'S WAY BACK. Derived from the route (routes/route.ts's
  // parentRoute), never from a per-screen prop, so a screen cannot forget it
  // and no state inside a screen can hide it. Rendered ONLY in the phone
  // stack below — the rail has no top bar, and inside the two-pane
  // master/detail "back" means de-select, which is the detail screen's own
  // business. `FrameBackProvider` tells every screen underneath that the duty
  // is taken, so nothing renders a second one.
  const frameParent = parentRoute(route);
  const frameBack =
    frameParent === null ? null : (
      <button
        type="button"
        class="app-topbar__back"
        data-testid="frame-back"
        onClick={() => navigate(routePath(frameParent))}
      >
        <span aria-hidden="true">‹</span> {routeLabel(frameParent)}
      </button>
    );

  // S12b Part C — the VISIBLE half of search, in both shells (rail and
  // phone), so the ⌘K accelerator is never the only way in. Rexx-only: the
  // sheet reads Rexx registries, and rendering it on Actor would put a
  // control on screen that searches nothing. An Actor user therefore sees
  // no change at all — same corner, same two buttons, same ⌘K.
  const findChrome =
    isRexx && findOpen ? (
      <FindSheet
        tenantId={tenantId}
        onNavigate={(to) => navigate(to)}
        onClose={() => setFindOpen(false)}
      />
    ) : null;

  // R1b — TWO-PANE (master-detail) on wide screens for the list→detail flows.
  // Built ONLY under isRail; phone renders the SAME routes full-screen below,
  // byte-identical to R1a. Two flows fit cleanly (both already have a list
  // route + a detail route in the route model): Auditions→AuditionDetail and
  // Projects→ProjectDetail. A bare list route (auditions/projects) shows the
  // list left + a calm empty state right; a detail route shows the parent
  // list left (selection highlighted) + that detail right. The detail screens
  // are reused VERBATIM — onBack de-selects (back to the list route), which
  // is the two-pane analogue of the phone's back-to-list. The Strip stays
  // functioning, pinned above the list pane. Other list screens (Today's
  // stream, Materials galleries, Career's money dashboard — no per-item
  // detail route) stay single-pane centred; masterDetail stays null for them.
  //
  // S5a: `railFrame` — not `isRail(breakpoint)` — is what the shell asks
  // from here down. The viewport still decides for everyone else; a tech is
  // simply never asked the question (see `techFrame` above).
  const railFrame = isRail(breakpoint) && !techFrame;

  let masterDetail: VNode | null = null;
  // A3: never on a household build. Both two-pane flows below are built from
  // the Actor pack's own screens (actor_audition / actor_project), so on
  // actor_assist a wide viewport would have silently replaced the registry
  // with a list of a namespace this tenant has none of — an empty pane that
  // looks like an empty household.
  if (railFrame && !isActorAssist) {
    if (route.screen === 'auditions' || route.screen === 'auditionDetail') {
      const selectedId = route.screen === 'auditionDetail' ? route.id : undefined;
      const listStage = route.screen === 'auditions' ? route.stage : 'all';
      masterDetail = (
        <MasterDetail
          strip={
            <Strip
              items={AUDITION_STAGES}
              activeId={route.screen === 'auditions' ? route.stage : undefined}
              onSelect={(stage) => navigate(routePath({ screen: 'auditions', stage }))}
            />
          }
          list={
            <Auditions
              tenantId={tenantId}
              stage={listStage}
              selectedId={selectedId}
              onOpen={(id) => navigate(routePath({ screen: 'auditionDetail', id }))}
            />
          }
          detail={
            selectedId ? (
              <AuditionDetail
                tenantId={tenantId}
                id={selectedId}
                onBack={() => navigate(routePath({ screen: 'auditions', stage: listStage }))}
                onViewSource={(sourceId) =>
                  navigate(routePath({ screen: 'source', id: sourceId, from: routePath(route) }))
                }
              />
            ) : (
              <PlaceholderScreen eyebrow="Auditions" line="Select an audition to see its details." />
            )
          }
        />
      );
    } else if (route.screen === 'projects' || route.screen === 'projectDetail') {
      const selectedId = route.screen === 'projectDetail' ? route.id : undefined;
      const listPhase = route.screen === 'projects' ? route.phase : 'all';
      masterDetail = (
        <MasterDetail
          strip={
            <Strip
              items={PROJECT_PHASES}
              activeId={route.screen === 'projects' ? route.phase : undefined}
              onSelect={(phase) => navigate(routePath({ screen: 'projects', phase }))}
            />
          }
          list={
            <Projects
              tenantId={tenantId}
              phase={listPhase}
              selectedId={selectedId}
              onOpen={(id) => navigate(routePath({ screen: 'projectDetail', id }))}
            />
          }
          detail={
            selectedId ? (
              <ProjectDetail
                tenantId={tenantId}
                id={selectedId}
                onBack={() => navigate(routePath({ screen: 'projects', phase: listPhase }))}
                onOpenAudition={(id) => navigate(routePath({ screen: 'auditionDetail', id }))}
                onViewSource={(sourceId) =>
                  navigate(routePath({ screen: 'source', id: sourceId, from: routePath(route) }))
                }
              />
            ) : (
              <PlaceholderScreen eyebrow="Projects" line="Select a project to see its details." />
            )
          }
        />
      );
    }
  }

  // C1 (decision ce1c3046) — THE CHASSIS IS THE CHROME, AND ONLY THE CHROME.
  // The navigation zone (side rail on desktop, NavZone on the phone) is the
  // one surface a pack may dress: it goes inside <Chassis>, which paints
  // --surface-chassis plus whatever material the active skin names. Everything
  // else — .app-main, .app-content, every screen — is the LIT DISPLAY FACE and
  // stays on --surface-display/-raised, because carbon may carry a headline
  // and may never carry a table. <OperatorSkin> mounts with this chrome and
  // nothing else, which is what keeps the portal routes above (they return
  // long before this line) chassis-free and material-free.
  const operatorSkin = <OperatorSkin vertical={effectiveVertical} />;

  // S1 — the cockpit shell. atlas-cockpit (the column/bar/sheet) is mounted
  // ONCE, around whichever of the two frames below actually renders, and
  // ONLY for the personal pack — every other pack's tree is returned
  // byte-identical to before this chunk. Wrapping here (rather than inside
  // each frame) is what lets the column's own state survive a tab switch:
  // this call site is outside the part of the tree route changes replace.
  // H4a (COCKPIT-HOME-COLUMNS-SPEC.md §H4) — on a project screen whose slug
  // carries a builder pane, the cockpit pane's occupant is `builder:<slug>`;
  // every other screen (including Origin Care's own project screen, which
  // has no pane assigned) keeps the default 'atlas'.
  const cockpitOccupant =
    route.screen === 'projectScreen' && isBuilderProjectSlug(route.slug) ? (`builder:${route.slug}` as const) : 'atlas';

  const wrapCockpit = (content: VNode): VNode =>
    isPersonal ? (
      <CockpitFrame
        tenantId={tenantId}
        occupant={cockpitOccupant}
        context={{ screen: route.screen, recordIds: route.screen === 'threadDetail' ? [route.id] : [] }}
        onNavigate={(key) => {
          // The second lock: only a surface this pack's switcher actually has.
          const surface = cockpitSurfaces(packVertical).find((s) => s.key === key);
          if (surface) navigate(surface.route);
        }}
      >
        {content}
      </CockpitFrame>
    ) : content;

  // TABLET/DESKTOP: rail on the left. Two-pane list→detail flows fill the row
  // with the MasterDetail (its own panes scroll); every other screen keeps the
  // R1a single-pane treatment — strip promoted into the content header, the
  // content column capped + centred (never left-pinned).
  if (railFrame) {
    return wrapCockpit(
      <div class={`app-shell app-shell--rail${isPersonal ? ' app-shell--cockpit' : ''}`}>
        {operatorSkin}
        {/* S1c — NO GLOBAL RAIL on the personal pack: the title switcher
            (mounted below, inside app-main) replaces it. Every other pack's
            Chassis/SideRail is untouched. */}
        {isPersonal ? null : (
          <Chassis class="app-chrome app-chrome--rail">
            <SideRail
              {...navProps}
              accountEntry={<AccountEntry variant="rail" onNavigate={onAccountNavigate} />}
              // B10 §4 — the desktop door to Your page, from the SAME
              // `moreExtra` source MoreSheet already renders (built above,
              // actor_assist only), so the phone's More sheet and the rail
              // can never disagree about what "Your page" points at.
              extra={moreExtra}
            />
          </Chassis>
        )}
        {masterDetail ? (
          <div class="app-main">{masterDetail}</div>
        ) : (
          <div class="app-main">
            {/* S1c — removing SideRail also removed the ONLY place the rail
                ever rendered AccountEntry (its foot — SideRail's own
                `accountEntry` prop). atlas-review caught this as a stray
                missing gear on the desktop personal pack: restored here,
                beside the switcher, floating-variant (the rail's own static
                foot placement has nowhere left to sit).
                ⚑ TOPBAR BRIEF (2026-10-15, Huan's Mail-screen feedback) —
                GlobalCreate ("+") moved INTO this same row, rather than
                staying in `topRightEntries` below: that fragment's rail
                placement is a FLOATING corner pinned over the gutter beside
                another pack's capped+centred content column (see
                global.css's own header on `.global-create`) — a gutter the
                cockpit's own full-width content (`.app-shell--cockpit`
                below) no longer has. Suppressed for isPersonal just below
                so the "+" is never drawn twice.
                ⚑ MAIL-POLISH BRIEF (2026-10-16, Huan's own words: "remove
                the settings and + buttons … don't add any real benefit …
                free up more space") — the gear ITSELF is gone from this
                row: Settings already has its own door in the title
                switcher's own menu (cockpitSurfaces' 'settings' group), so
                this was a second path to the same place, costing width for
                no benefit.
                ⚑ Addition, same brief, same day, Huan's final word — the
                "+" STAYS but goes CONTEXTUAL rather than always opening the
                global create menu: Mail -> Compose, Calendar -> New event,
                every other cockpit screen -> the global menu, unchanged.
                `cockpitCreate` below is the one place that mapping lives —
                see it just above this JSX. */}
            {cockpitTitleSwitcher ? (
              <div class="app-cockpit-titlebar">
                <div class="app-cockpit-titlebar__left">
                  {cockpitHomeMark}
                  {cockpitTitleSwitcher}
                </div>
                <div class="app-cockpit-titlebar__right">
                  <GlobalCreate onClick={cockpitCreate.onClick} ariaLabel={cockpitCreate.ariaLabel} />
                </div>
              </div>
            ) : null}
            {strip ? <div class="app-subnav">{strip}</div> : null}
            <main class={`app-content${cockpitFillScreen ? ' app-content--fill' : ''}`}>{screen}</main>
          </div>
        )}
        {isPersonal ? null : topRightEntries}
        {captureSheet}
        {recordsChrome}
        {findChrome}
      </div>
    );
  }

  // PHONE: a single column — the frame's own top bar, then the screen
  // scrolling inside .app-content, then the strip + bottom nav pinned at the
  // foot inside the NavZone.
  //
  // CV4 §3: the top bar is new, and it is the whole fix. The gear, the search
  // target and the "+" used to float over the column at z-index 40, covering
  // (and stealing the taps from) the first top-left/top-right control on ten
  // screens — every one of them a BackLink, Account's own included. They are
  // ordinary children of a flex row now, which is what the desktop rail has
  // always done with the gear. Nothing overlaps because nothing floats.
  //
  // CV5 §2: and the bar gains the way back, in flow beside the gear — never
  // absolute, never over the content (CV4 §3's rule, unregressed).
  return wrapCockpit(
    <FrameBackProvider owns={frameBack !== null}>
      <div class="app-shell app-shell--stack">
        {operatorSkin}
        <div class="app-topbar">
          {frameBack}
          <AccountEntry onNavigate={onAccountNavigate} />
          <div class="app-topbar__gap" />
          <div class="app-topbar__right">{topRightEntries}</div>
        </div>
        {/* S1c — the title switcher is ADDITIONAL on the phone, mounted
            above the content, BESIDE the existing bottom bar — never in
            place of it. Brief's own ⚑ "Do NOT touch": the phone's bottom
            bar and the Knot stay EXACTLY as they are (Huan is deciding
            where capture lives next, separately). An earlier cut of this
            chunk removed NavZone/the Knot here too; atlas-review caught it
            (2026-10-15) — AppCockpitPhoneShell.test.tsx pins this. */}
        {isPersonal && cockpitTitleSwitcher ? (
          <div class="app-cockpit-titlebar app-cockpit-titlebar--stack">
            <div class="app-cockpit-titlebar__left">
              {cockpitHomeMark}
              {cockpitTitleSwitcher}
            </div>
          </div>
        ) : null}
        <main class="app-content">{screen}</main>
        <Chassis class="app-chrome app-chrome--stack">
          {/* MERGE 2026-08-26: the phone split now lives in useNavModel
              (declared phoneTabs, else the derived landing-group split), so
              navProps.destinations IS the bar and overflow rides navProps.
              UNCHANGED for the personal pack by S1c — see the note above. */}
          <NavZone {...navProps} strip={strip} />
        </Chassis>
        {captureSheet}
        {recordsChrome}
        {findChrome}
        {/* MERGE 2026-08-26: trunk's overflow mechanism (useNavModel derives
            it; splitPhoneRail retired below). The subject-scope grafting
            this comment used to describe is gone with the scope itself
            (2026-09-19 ruling) — a row's href is just its own route now. */}
        {moreOpen && (overflow.length > 0 || moreExtra.length > 0) ? (
          <MoreSheet
            destinations={overflow}
            active={activeTab}
            onNavigate={navProps.onNavigate}
            onClose={() => setMoreOpen(false)}
            badges={navProps.badges}
            extra={moreExtra}
          />
        ) : null}
      </div>
    </FrameBackProvider>
  );
}
